import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DOWN_PAYMENT_DUE_PHRASE } from '../constants/downPaymentDue';
import GuidedLetter, { useGuidedLetterCopy } from '../components/guided/GuidedLetterFields';
import EditApplicationCard from '../components/guided/EditApplicationCard';
import JourneyMainCard from '../components/dashboard/JourneyMainCard';
import { guidedColumn } from '../constants/guidedLayout';
import { CHECK_COLOURS, checklistItemColours, waitingIndex, isOverdue } from '../constants/journeySteps';
import axios from 'axios';
import AppShell from '../components/AppShell';
import SmartEstimatePreview from '../components/SmartEstimatePreview';
import LoanReference from '../components/LoanReference';
import TrustpilotPrompt from '../components/TrustpilotPrompt';
import AppStoreBadge from '../components/AppStoreBadge';
import GooglePlayBadge from '../components/GooglePlayBadge';
import { isNativeApp } from '../utils/isNativeApp';
import WireInstructionsModal from '../components/WireInstructionsModal';
import { useAuth } from '../context/AuthContext';
import { useSurvey } from '../context/SurveyContext';
// LOXForm import removed alongside the Credit Conditions block in the
// In-Review hero. Re-import if a future surface needs the inline LOX
// writer back on the borrower Dashboard.
import PaymentModal from '../components/PaymentModal';
import PSASigningModal from '../components/PSASigningModal';
import OfferAcceptedReview from '../components/OfferAcceptedReview';

import { LOX_MIN_CHARS } from '../constants/lox';
// 🚨 BORROWER SCREEN: visibility, never isOpenNeed — an unproven dismissal is
// open to staff and must not reach the borrower (Jesse, 2026-10-14).
import { isVisibleToBorrower, isAwaitingReview } from '../constants/needStatus';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── Document checklist (dynamic by employment type) ──────────────────────────
function getDocChecklist(employment) {
  const base = [
  { id: 'bank_stmts', label: 'Bank Statements', desc: 'Last 2 months, all accounts' },
  ];
  switch ((employment || '').toLowerCase().replace(/[- ]/g, '_')) {
  case 'self_employed':
  return [
  ...base,
  { id: 'pl', label: 'P&L Statement (YTD)', desc: 'Prepared by a licensed CPA' },
  { id: '1099s', label: '1099s', desc: 'All 1099 forms for the past 2 years' },
  ];
  case 'retired':
  return [
  ...base,
  { id: 'ss_award', label: 'Social Security Award Letter', desc: 'Shows current monthly benefit' },
  { id: 'pension', label: 'Pension Statement', desc: 'Most recent statement' },
  { id: 'retirement_stmts', label: 'Retirement Account Statements', desc: 'Last 2 months' },
  ];
  case 'military':
  return [
  ...base,
  { id: 'les', label: 'LES Leave and Earnings Statement', desc: 'Most recent, shows base pay' },
  { id: 'va_award', label: 'VA Award Letter', desc: 'If receiving VA disability compensation' },
  ];
  default: // W2, employee, or unknown
  return [
  ...base,
  { id: 'pay_stubs', label: 'Pay Stubs', desc: 'Most recent 30 days, shows YTD earnings' },
  { id: 'w2s', label: 'W-2s', desc: 'Last 2 years' },
  ];
  }
}

// ─── Hero components ──────────────────────────────────────────────────────────
// 🔑 ONE "UNDER REVIEW" CARD FOR EVERY HELD OR IN-REVIEW FILE (Jesse, 2026-10-29 — found by e2e review-not-disclosed).
// /api/borrower/status sets under_review for a fraud review AND for every held state (utils/decisionHolds), so this card
// — one component, one copy, one style, shown from the same status read — never tells one kind of hold from another.
// It names no reason, no workflow and no timescale (a lender's VOM can take longer than a day).
function HeroUnderReview({ name }) {
  const first = name ? String(name).split(' ')[0] : '';
  return (
    <div data-testid="dashboard-under-review" className="mb-8">
      <div className="rounded-2xl border shadow-sm p-5 sm:p-8" style={{ background: '#fffbeb', borderColor: '#ffdd86' }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 84, height: 84, borderRadius: 9999, background: '#ffdd86' }}>
            <span style={{ fontSize: 38, lineHeight: 1 }} role="img" aria-label="under review">⏳</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#111827' }}>
              {first ? `${first}, your application is under review` : 'Your application is under review'}
            </h1>
            <p className="mt-2 mb-6 leading-relaxed" style={{ color: '#374151' }}>
              Your decision will show on your application steps as soon as it&rsquo;s ready, and we&rsquo;ll send you an alert.
            </p>
            <Link to="/apply/guided" className="btn-primary text-white hover:opacity-90" style={{ backgroundColor: '#3a6ad9' }}>
              <span>Go to my application</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// 🔑 THE NEUTRAL FALLBACK (cleanup Phase 2): no status, or a pre-decision state — the application is on the guided
// steps, so this sends the borrower back there. No decision language, no promise.
function HeroNoApp() {
  return (
  <div data-testid="dashboard-your-application" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 mb-8">
  <div className="max-w-xl">
  <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">
  Your application
  </h1>
  <p className="text-gray-500 mb-6 leading-relaxed">
  Your application and its next step are on your application steps.
  </p>
  <Link
  to="/apply/guided"
  className="btn-primary text-white hover:opacity-90"
  style={{ backgroundColor: '#3a6ad9' }}
  >
  <span>Go to my application</span>
  <span className="btn-arrow">
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
  </span>
  </Link>
  </div>
  </div>
  );
}

// REVIEW_SECTION_META + SECTION_STATUS_MAP + ReviewSectionCard removed
// alongside the Review Sections grid block in the In-Review hero — they
// were the icon/status map + card renderer the grid consumed. Re-introduce
// from a separate module if a future surface needs the per-section status
// pill card back.

// ── HeroPaused — neutral interim hero for guarded terminal/paused states ──────
// Rendered for application_status 'not_moving_forward' and 'on_hold' (routed here
// by the backend TERMINAL_PAUSED_STAGES guard so they can't fall through to the
// old CRM lead stage's live ACTION hero). INTENTIONALLY MINIMAL: no down-payment
// / signing / payment CTA, no "start your application" / restart, no dollar
// amounts, no deadlines, and nothing about WHY the file paused or whether it can
// resume — real copy (withdrawn vs declined split) replaces this later. Uses the
// same support contact affordance as the other heroes. adverse / adverse_pending
// are NOT routed here — they render their own legally-governed heroes.
// ⚠️ ONE BUTTON, TWO HEROES, AND IT NEVER PROMISES MORE THAN IT DOES. The
// server refuses a reapply the dashboard should not have offered — a hidden CTA
// is not a gate — so this renders whatever the server says rather than assuming
// success. On refusal it shows the server's sentence, because the server is the
// only thing that knows why.
function ReapplyButton({ label }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      const token = localStorage.getItem('doorly_token');
      await axios.post(`${API_URL}/api/borrower/reapply`, {}, { headers: { Authorization: `Bearer ${token}` } });
      // A full reload rather than a state update: reopening a cycle changes
      // almost every field the dashboard reads, and re-deriving them one at a
      // time is how a stale hero survives its own file becoming active.
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.message || 'We could not start a new application just now. Nothing has changed on your file.');
      setBusy(false);
    }
  };
  return (
    <div className="mt-4">
      <button onClick={go} disabled={busy}
        style={{ padding: '11px 20px', borderRadius: 10, border: 'none', background: '#3a6ad9',
          color: '#fff', fontSize: 15, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Starting…' : label}
      </button>
      {error && <p className="mt-2" style={{ color: '#ff082f', fontSize: 13.5, lineHeight: 1.6 }}>{error}</p>}
    </div>
  );
}

function HeroPaused({ name }) {
  const first = name ? String(name).split(' ')[0] : '';
  return (
    <div className="mb-8">
      <div className="rounded-2xl border shadow-sm p-5 sm:p-8" style={{ background: '#f9fafb', borderColor: '#e5e7eb' }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 84, height: 84, borderRadius: 9999, background: '#e5e7eb' }}>
            <span style={{ fontSize: 38, lineHeight: 1 }} role="img" aria-label="on hold">⏸️</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#111827' }}>
              {first ? `${first}, your file isn't progressing right now` : "Your file isn't progressing right now"}
            </h1>
            {/* ⚠️ AN ARCHIVED FILE WAS NEVER DECIDED AGAINST, and the copy has to
                carry that difference. "Start a new application" — not "apply
                again", which implies a previous refusal. The two things a
                borrower needs to know are that their documents are safe and
                that credit is pulled fresh; both are stated rather than
                discovered on the next screen. */}
            <p className="mt-2 leading-relaxed" style={{ color: '#374151' }}>
              We closed this application because it went quiet, but nothing is lost — your documents are
              still here and you won&rsquo;t need to upload them again.
            </p>
            <p className="mt-2 leading-relaxed" style={{ color: '#374151' }}>
              When you&rsquo;re ready, start a new application. We&rsquo;ll re-check your documents and pull fresh
              credit, so everything reflects where you are now.
            </p>
            <ReapplyButton label="Start a new application" />
            <p className="mt-3 leading-relaxed" style={{ color: '#6b7280', fontSize: 14 }}>
              Questions? Email <a href="mailto:support@godoorly.com" style={{ color: '#3a6ad9' }}>support@godoorly.com</a> or call (512) 713-6016.
            </p>
            <LoanReference />
          </div>
        </div>
      </div>
    </div>
  );
}

// DashboardOpenNeedsPanel — standalone Additional Needs surface that
// renders at the top of the Dashboard regardless of which hero is
// active. Fires whenever the borrower has any non-resolved
// additional_needs row (admin-driven flags from the Underwriting
// Review Module's per-section + per-LOX flags, AND AI-driven needs
// from ReadyCheck). Without this, post-approval borrowers (status
// home_shopping / option_period / offer_submitted / under_contract
// etc.) couldn't see the admin's flag on their Dashboard at all —
// the In-Review hero panel only fired when they were still in the
// pre-approval stages.
function DashboardOpenNeedsPanel({ token }) {
  // 🔑 The guided letter (approved by Hannah 2026-11-02) replaces the inline free-text box on a letter of explanation.
  const guidedCopy = useGuidedLetterCopy(token);
  const [needs, setNeeds] = React.useState([]);
  // Inline LOX response state keyed by need.id so each credit-card
  // has independent textarea/submit lifecycle. { text, busy, error }
  const [loxResp, setLoxResp] = React.useState({});
  const refetch = React.useCallback(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API_URL}/api/review/sections`, { headers })
      .then(r => setNeeds((r.data?.additional_needs || []).filter(isVisibleToBorrower)))
      .catch(() => {});
  }, [token]);
  React.useEffect(() => { refetch(); }, [refetch]);
  if (needs.length === 0) return null;
  // 🔑 ONE NUMBER, MIRRORED FROM THE SERVER. This file and Dashboard.js each
  // held their own `20`, and the route enforced nothing — see constants/lox.js.
  const LOX_MIN = LOX_MIN_CHARS;
  return (
    <div className="bg-white border border-amber-200 rounded-2xl p-5 sm:p-6 shadow-sm mb-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-amber-600 text-xl">⚠</span>
        <h2 className="text-base font-bold text-gray-900">
          {needs.length} {needs.length === 1 ? 'item needs' : 'items need'} your attention
        </h2>
      </div>
      <div className="space-y-2 mb-4">
        {needs.slice(0, 8).map(n => {
          const isResponded = isAwaitingReview(n);
          // Credit-category needs with a linked_condition_id resolve
          // via an inline LOX response form right on the card —
          // borrower doesn't need to navigate into the ReadyCheck
          // wizard to type. Credit rows without a linked_condition_id
          // (defensive — shouldn't happen post-migration) fall back
          // to the "go to ReadyCheck" copy.
          const isCredit = n.category === 'credit';
          const canRespondInline = isCredit && !!n.linked_condition_id && !isResponded;
          const state = loxResp[n.id] || { text: '', busy: false, error: null };
          const text = state.text || '';
          const tooShort = text.length < LOX_MIN;
          const submitLox = async () => {
            if (tooShort || state.busy) return;
            setLoxResp(p => ({ ...p, [n.id]: { ...state, busy: true, error: null } }));
            try {
              await axios.post(`${API_URL}/api/documents/lox`,
                { condition_id: n.linked_condition_id, explanation_text: text },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setLoxResp(p => ({ ...p, [n.id]: { text: '', busy: false, error: null } }));
              refetch();
            } catch (err) {
              const msg = err?.response?.data?.error || err?.message || 'Could not submit your response. Please try again.';
              setLoxResp(p => ({ ...p, [n.id]: { ...state, busy: false, error: msg } }));
            }
          };
          return (
            <div
              key={n.id}
              className="rounded-xl p-3 border"
              style={{
                background: isResponded ? '#fffbeb' : '#fffbef',
                borderColor: isResponded ? '#fde68a' : '#ffdd86',
              }}
            >
              <div className="flex items-start gap-2">
                {n.category && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded flex-shrink-0 mt-0.5"
                    style={{ background: '#fff', color: '#7a5200', border: '1px solid #ffdd86' }}
                  >
                    {n.category}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  {/* 🚨 The need's own title — never the staff condition title (the server no longer sends it;
                      a sent-back letter's title is the approved card title, backend utils/loxSendBack). */}
                  <p className="text-sm font-semibold text-gray-900 leading-snug">
                    {n.title || n.description}
                  </p>
                  {/* Original underwriter ask — only on credit needs
                      with a linked LOX. Surfaces the long-form
                      description from credit_conditions so the
                      borrower has the full context of what was asked
                      originally. */}
                  {isCredit && n.linked_lox_description && (
                    <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                      {n.linked_lox_description}
                    </p>
                  )}
                  {/* Non-credit needs still show their own description
                      (which is the actual ask text for AI/doc-review
                      needs). For credit needs, the description is the
                      admin's flag note — surfaced separately below as
                      "Comments from Underwriter". */}
                  {!isCredit && n.title && n.description && n.title !== n.description && (
                    <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                      {n.description}
                    </p>
                  )}
                  {/* Comments from Underwriter — admin's flag note,
                      stored in additional_needs.description on credit
                      flags. Labeled distinctly so the borrower
                      understands it's the underwriter's specific
                      "here's what to improve" guidance, separate from
                      the original LOX ask. */}
                  {isCredit && n.description && (
                    <div className="mt-2 rounded-md p-2 text-xs leading-relaxed" style={{ background: '#fff', border: '1px solid #fde68a', borderLeft: '3px solid #92400e' }}>
                      <div className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: '#92400e' }}>
                        Comments from Underwriter
                      </div>
                      <div className="text-gray-700">{n.description}</div>
                    </div>
                  )}
                  {isResponded ? (
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mt-1.5">
                      ✓ Responded — awaiting review
                    </p>
                  ) : !canRespondInline && (
                    <p className="text-[10px] font-medium text-gray-500 mt-1.5">
                      {isCredit
                        ? 'Action: respond on your Documents page.'
                        : 'Action: upload the requested document on your Documents page.'}
                  </p>
                  )}
                </div>
              </div>
              {/* Inline LOX response form — credit needs only. The
                  full LOX flow happens here without forcing the
                  borrower into the wizard. */}
              {canRespondInline && guidedCopy && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <GuidedLetter g={guidedCopy} busy={state.busy ? 'text' : null} testid="dashboard-need-guided"
                    onSend={async (body) => {
                      setLoxResp(p => ({ ...p, [n.id]: { ...state, busy: true, error: null } }));
                      try {
                        await axios.post(`${API_URL}/api/documents/lox`, { condition_id: n.linked_condition_id, ...body }, { headers: { Authorization: `Bearer ${token}` } });
                        setLoxResp(p => ({ ...p, [n.id]: { text: '', busy: false, error: null } }));
                        refetch();
                      } catch (err) {
                        setLoxResp(p => ({ ...p, [n.id]: { ...state, busy: false, error: err?.response?.data?.message || 'Could not submit your response. Please try again.' } }));
                      }
                    }} />
                  {state.error && (
                    <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">{state.error}</div>
                  )}
                </div>
              )}
              {canRespondInline && guidedCopy === null && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <label className="block text-[11px] font-semibold text-amber-900 mb-1">Your response</label>
                  <textarea
                    rows={4}
                    value={text}
                    onChange={e => setLoxResp(p => ({ ...p, [n.id]: { ...state, text: e.target.value } }))}
                    placeholder="Type your explanation here…"
                    className="w-full rounded-lg px-3 py-2 text-sm resize-none"
                    style={{
                      border: '1px solid #fde68a',
                      background: '#fff',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <p className={`text-xs ${tooShort ? 'text-amber-700' : 'text-green-700 font-medium'}`}>
                      {text.length} / {LOX_MIN} characters minimum
                    </p>
                  </div>
                  {state.error && (
                    <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">
                      {state.error}
                    </div>
                  )}
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={submitLox}
                      disabled={tooShort || state.busy}
                      className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-40"
                      style={{ backgroundColor: '#3a6ad9' }}
                    >
                      {state.busy ? 'Submitting…' : 'Submit Response →'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {needs.length > 8 && (
          <p className="text-xs text-gray-500 italic">
            + {needs.length - 8} more — see your Documents page for the full list
          </p>
        )}
      </div>
      <Link
        to="/documents#action-needed"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
        style={{ background: '#3a6ad9', color: '#fff' }}
      >
        Go to Documents →
      </Link>
    </div>
  );
}

// ── Option Period Dashboard ───────────────────────────────────────────────────

// (Option Period progress bar removed — checklist items are not strictly
// linear, so the OP_TIMELINE / opTimelineStep helpers that drove the bar
// are gone. The checklist below is now the canonical progress surface.)

function HeroOptionPeriod({ name, token, isCoBorrower, primaryName, onStageReset }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [acting, setActing]       = useState(false);
  const [decisionChoice, setDecisionChoice] = useState('');
  const [showCongratsPopup, setShowCongratsPopup] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const [showWireEMD, setShowWireEMD] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionTab, setInspectionTab] = useState('review');
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  // Optional inspection-report attachment for the seller credit request
  // — paired with the "Independent Home Inspection" copy on the
  // option-period dashboard. File is uploaded via
  // /api/option-period/upload-inspection-report (multipart) which
  // returns a documentId we then pass into /credit-request.
  const [inspectionReportFile, setInspectionReportFile] = useState(null);
  const [creditSubmitting, setCreditSubmitting] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditReason, setCreditReason] = useState('');
  const [creditSent, setCreditSent] = useState(false);
  // Option-period withdrawal modal — 2 steps (warning → confirm). Cancel
  // ("keep my home") is the primary CTA on step 1 so withdrawal requires
  // deliberate effort. Step 2 requires the borrower to acknowledge the
  // option-fee charge + down-payment-credit consequences via checkbox.
  const [withdrawStep, setWithdrawStep] = useState(0);
  const [withdrawAck, setWithdrawAck] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  // B-C PSA in-app canvas signing modal (replaces the legacy BoldSign
  // multi-signer popup flow). Opened from the SignCard CTA below; closes
  // and refetches dashboard state on successful signature submission.
  const [psaModalOpen, setPsaModalOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
  if (!token) return;
  axios.get(`${API_URL}/api/option-period/dashboard`, { headers })
  .then(r => setData(r.data))
  .catch(() => {})
  .finally(() => setLoading(false));
  };

  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, body = {}) => {
  setActing(true);
  let postResp = null;
  try {
  postResp = await axios.post(`${API_URL}/api/option-period/${path}`, body, { headers });
  const r = await axios.get(`${API_URL}/api/option-period/dashboard`, { headers });
  setData(r.data);
  } catch (_) {}
  setActing(false);
  return postResp?.data || null;
  };

  // Congrats popup — show once
  useEffect(() => {
  if (!data?.option_period) return;
  const key = 'op_congrats_' + data.option_period.id;
  if (!localStorage.getItem(key)) {
    setShowCongratsPopup(true);
    setConfettiActive(true);
    localStorage.setItem(key, '1');
    setTimeout(() => setConfettiActive(false), 5000);
  }
  }, [data?.option_period?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
  return (
  <div className="mb-8 space-y-4 animate-pulse">
  <div className="h-32 bg-gray-200 rounded-2xl" />
  <div className="grid grid-cols-2 gap-4">
  <div className="h-28 bg-gray-100 rounded-xl" />
  <div className="h-28 bg-gray-100 rounded-xl" />
  </div>
  </div>
  );
  }

  const op      = data?.option_period || {};
  const tasks   = data?.tasks || {};
  const kd      = data?.key_dates || {};
  const daysRem = data?.days_remaining;
  const wire    = data?.wire_instructions || null;

  // Legacy fallback for borrowers who confirmed via the old /emd endpoint
  // before the down_payment_* columns existed. New flow reads
  // data.down_payment_sent / data.down_payment_verified from /dashboard.
  const emdDone        = !!tasks.emd_wired;
  const appraisalDone  = op.appraisal_status && op.appraisal_status !== 'unpaid';
  const inspectionDone = op.inspection_status && op.inspection_status !== 'unpaid';
  const inspAppraisalOrdered = !!op.inspection_appraisal_ordered || (appraisalDone && inspectionDone);
  // LE acknowledgment used to gate a checklist item here; that item has
  // moved to the offer-accepted review overlay (Screen 1). leDone is no
  // longer read in HeroOptionPeriod.
  const decisionDone   = !!tasks.decision;

  // The property and its price are the page's main card now (JourneyMainCard, 2026-11-02).

  // formattedEnd / endDate were only used by the removed Hero Banner
  // KPI; the dedicated Option Period Remaining card formats its own
  // end-date footer from op.option_end_date / kd.option_period_end.

  const firstName = name ? name.split(' ')[0] : '';
  const fmt = (v) => v ? '$' + Number(v).toLocaleString() : null;

  // Confetti
  const confettiPieces = confettiActive ? Array.from({ length: 50 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  delay: Math.random() * 3,
  dur: 2 + Math.random() * 3,
  color: ['#acf44e', '#3a6ad9', '#fffd5b', '#ff082f', '#3a6ad9', '#fff'][Math.floor(Math.random() * 6)],
  size: 6 + Math.random() * 8,
  })) : [];

  // ───────────────────────────────────────────────────────────────────────────
  // CO-BORROWER VIEW — read-only dashboard. The primary borrower drives the
  // EMD wire, inspection/appraisal order, and the move-forward/terminate call.
  // The co-borrower's active responsibility is signing the LE and PSA.
  // ───────────────────────────────────────────────────────────────────────────
  if (isCoBorrower) {
    // Tracks which report card is expanded inline (primary uses a modal; co-borrower sees text in place)
    const openReport = inspectionTab === 'insp' || inspectionTab === 'app' ? inspectionTab : null;
    const toggleReport = (kind) => setInspectionTab(openReport === kind ? 'review' : kind);

    // SignCard component removed — its only call site (the co-borrower's
    // PSA card) was rewritten inline below to fire setPsaModalOpen
    // directly instead of routing through the legacy psa_url URL-entry
    // path. The native e-sign flow (PSASigningModal →
    // /api/option-period/sign-bc-psa-canvas) doesn't need a URL.
    const ReportCard = ({ label, reportText, kind }) => {
      const expanded = openReport === kind;
      return (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 12, padding: 16, marginBottom: 10,
          opacity: op.inspection_appraisal_ordered ? 1 : 0.6,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {op.inspection_appraisal_ordered
                  ? (reportText ? 'Report delivered' : 'Ordered — awaiting delivery')
                  : 'Not yet ordered'}
              </div>
            </div>
            {reportText ? (
              <button onClick={() => toggleReport(kind)}
                style={{
                  padding: '8px 14px', background: '#edf1fb',
                  border: '1px solid #c5d3f5', borderRadius: 8,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#3a6ad9',
                  flexShrink: 0,
                }}>
                {expanded ? 'Hide' : 'View Report →'}
              </button>
            ) : (
              <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>
                {op.inspection_appraisal_ordered ? 'Pending delivery' : 'Pending order'}
              </span>
            )}
          </div>
          {expanded && reportText && (
            <div style={{
              marginTop: 12, padding: 12, background: '#f9fafb',
              border: '1px solid #f3f4f6', borderRadius: 8,
              fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6,
            }}>
              {reportText}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="mb-8 space-y-4" style={{ maxWidth: 680, margin: '0 auto' }}>
        {/* Status banner */}
        <div style={{
          ...CHECK_COLOURS.card,
          borderRadius: 16, padding: '20px 24px',
          display: 'flex', gap: 14, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#3a6ad9',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
              You're in the Option Period
            </div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              {primaryName
                ? `${primaryName} is managing the active steps for this file. Your role is to review and sign the Loan Estimate and Purchase and Sale Agreement.`
                : 'The primary borrower is managing the active steps. Your role is to review and sign key documents.'}
            </div>
          </div>
        </div>

        {/* Property + countdown: the page's main card (JourneyMainCard), 2026-11-02. */}

        {/* Your documents to sign */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            Your Documents to Sign
          </div>
          {/* LE SignCard removed — Loan Estimate is now reviewed in the
              offer-accepted overlay (Screen 1) before option_period.
              ────────────────────────────────────────────────────────
              PSA card no longer reads op.psa_url (the legacy URL-entry
              column the admin Option Period Documents card used to
              write). The native e-sign flow fires PSASigningModal
              directly, which fetches the pristine PDF via
              /api/option-period/bc-psa-pdf and signs via
              /api/option-period/sign-bc-psa-canvas. No URL plumbing
              needed. Three visible states:
                · signed   → green '✓ Signed {date}' pill
                · awaiting → 'Review & Sign →' button
                · package terminal-fail → handled in the primary's
                  checklist item; co-borrower view defers to the
                  main dashboard refresh.
              ──────────────────────────────────────────────────────── */}
          {(() => {
            const signedAt = op.psa_signed_coborrower_at;
            if (signedAt) {
              const shortDate = (v) => {
                try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
                catch { return ''; }
              };
              return (
                <div style={{
                  background: '#fff', border: '1px solid #d4f99a',
                  borderRadius: 12, padding: 16, marginBottom: 10,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>Purchase and Sale Agreement (PSA)</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Signed {shortDate(signedAt)}</div>
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 12px',
                    borderRadius: 20, background: '#f4fee8', color: '#2d6a0a',
                    border: '1px solid #d4f99a', flexShrink: 0,
                  }}>✓ Signed</span>
                </div>
              );
            }
            return (
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 12, padding: 16, marginBottom: 10,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>Purchase and Sale Agreement (PSA)</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Ready for your signature</div>
                </div>
                <button onClick={() => setPsaModalOpen(true)}
                  style={{
                    background: '#3a6ad9', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 20px', fontSize: 14,
                    fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  }}>
                  Review & Sign →
                </button>
              </div>
            );
          })()}
          {/* Co-borrower context line — surfaces the primary borrower's
              signing status under the card so the co-borrower understands
              what's outstanding even before they open the modal. */}
          {op.psa_signed_primary_at && op.psa_signed_coborrower_at ? (
            <div style={{
              marginTop: -4, marginBottom: 10, padding: '6px 12px',
              background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 8,
              fontSize: 12, fontWeight: 700, color: '#2d6a0a',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              ✓ Both borrowers have signed the PSA
            </div>
          ) : op.psa_signed_primary_at && !op.psa_signed_coborrower_at ? (
            <div style={{
              marginTop: -4, marginBottom: 10, padding: '6px 12px',
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
              fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
              {primaryName || 'The primary borrower'} has signed — your signature is needed to complete the contract.
            </div>
          ) : null}
        </div>

        {/* Reports — view only */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            Reports (View Only)
          </div>
          <ReportCard label="Inspection Report" reportText={op.inspection_report_text} kind="insp" />
          <ReportCard label="Appraisal Report"  reportText={op.appraisal_report_text}  kind="app"  />
        </div>

        {/* What the primary is handling */}
        <div style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            {primaryName ? `${primaryName} is handling` : 'Primary borrower is handling'}
          </div>
          {[
            'Down Payment wire',
            'Ordering your appraisal package',
            'Reviewing reports and making the final decision',
            'Title company coordination',
          ].map((item, i, arr) => (
            <div key={i} style={{
              fontSize: 13, color: '#6b7280', padding: '5px 0',
              borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ color: '#d1d5db' }}>—</span> {item}
            </div>
          ))}
        </div>

        {/* Financial summary — view only */}
        {(op.purchase_price || op.loan_amount || op.down_payment) && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-sm font-bold text-gray-900 mb-4">💰 Financial Summary</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Purchase Price',       value: fmt(op.purchase_price) },
                { label: 'Loan Amount',          value: fmt(op.loan_amount) },
                { label: 'Down Payment',         value: fmt(op.down_payment) },
                { label: 'Est. Monthly Payment', value: op.monthly_payment ? `${fmt(op.monthly_payment)}/mo` : null },
              ].filter(i => i.value).map(({ label, value }) => (
                <div key={label} className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="text-base font-bold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
  <div className="mb-8 space-y-4">

  {/* ── Confetti ── */}
  {confettiActive && (
  <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10000, overflow: 'hidden' }}>
  <style>{`@keyframes confettiFall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
  {confettiPieces.map(p => (
    <div key={p.id} style={{ position: 'absolute', left: p.left + '%', top: '-20px', width: p.size, height: p.size, background: p.color, borderRadius: Math.random() > 0.5 ? '50%' : '2px', animation: `confettiFall ${p.dur}s ${p.delay}s ease-in forwards` }} />
  ))}
  </div>
  )}

  {/* ── Congrats Popup ── */}
  {showCongratsPopup && (
  <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
  <div style={{ background: '#fff', borderRadius: '20px', padding: '40px 36px', maxWidth: '460px', width: '100%', textAlign: 'center', boxShadow: '0 30px 80px rgba(0,0,0,0.3)' }}>
    <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
    <h2 style={{ margin: '0 0 12px', fontSize: '26px', fontWeight: '900', color: '#111827' }}>You're Under Contract!</h2>
    <p style={{ margin: '0 0 8px', fontSize: '16px', color: '#374151', lineHeight: '1.7' }}>
      Congratulations{firstName ? `, ${firstName}` : ''}! Doorly's offer was accepted and you're now in your option period.
    </p>
    <p style={{ margin: '0 0 28px', fontSize: '14px', color: '#6b7280', lineHeight: '1.7' }}>
      This is your time to complete key steps and confirm this is the right home for you.
    </p>
    {daysRem !== null && (
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '14px 20px', marginBottom: '24px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '24px' }}>⏳</span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '800', color: '#c2410c' }}>{daysRem} days left</div>
          <div style={{ fontSize: '12px', color: '#7a5200' }}>in your option period</div>
        </div>
      </div>
    )}
    <button onClick={() => setShowCongratsPopup(false)}
      style={{ width: '100%', padding: '14px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '800', fontSize: '16px' }}>
      Let's Do This! 🏡
    </button>
  </div>
  </div>
  )}

  {/* 🔑 THE HEADER IS THE PAGE'S MAIN CARD NOW (the Home Shopping page redesign, 2026-11-02): "Under contract ·
      [address] · $[price]" and "Your option period ends [Mon D] at 5:00 p.m." (components/dashboard/JourneyMainCard)
      replace the "Option Period Active" heading and its two tinted boxes (Your Home, days left). */}

  {/* ── YOUR CHECKLIST ── */}
  <div data-testid="dashboard-checklist" style={{ ...CHECK_COLOURS.card, borderRadius: '16px', padding: '22px', marginBottom: '16px' }}>
  <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Your Checklist</div>

  {[
    // 1. Down Payment (formerly "EMD" — borrower-facing copy is now
    //    "Down Payment"; the legacy emd_wired column is still written by
    //    POST /api/option-period/emd alongside the new down_payment_*
    //    columns so admin tooling continues to work.)
    {
      id: 'down-payment-checklist-item',
      // dpVerified beats dpSent beats emdDone (legacy fallback). The hero
      // shows three distinct UI states off these flags.
      done: !!data?.down_payment_verified || !!data?.down_payment_sent || emdDone,
      urgent: !(data?.down_payment_verified || data?.down_payment_sent || emdDone),
      urgentLabel: kd.emd_due_date ? `🔴 Due ${new Date(kd.emd_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '🔴 Due soon',
      // 🔑 Red only when it is overdue (the colour rule, 2026-11-02): the due date has passed and the wire is not sent.
      overdue: !(data?.down_payment_verified || data?.down_payment_sent || emdDone) && isOverdue(kd.emd_due_date),
      label: 'Send Down Payment',
      // 🔑 Due 3 business days after the effective date (constants/downPaymentDue, Jesse 2026-11-01) — was "immediately".
      description: `Secure your contract by sending your down payment via wire ${DOWN_PAYMENT_DUE_PHRASE}. The Purchase Initiation Deposit you already paid will be credited on your Closing Disclosure.`,
      content: (
        <div>
          {/* Rollover credit banner — visible only when a prior-transaction
              credit is available. Covers the wire amount entirely if
              credit ≥ wire owed, otherwise just informs the borrower the
              credit will be deducted from the wire amount. */}
          {data?.down_payment_credit_available && Number(data?.down_payment_credit_amount || 0) > 0 && !data?.down_payment_sent && !emdDone && (
            <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#166534' }}>
              💰 <strong>Credit Available:</strong> ${Number(data.down_payment_credit_amount).toLocaleString()} from your previous transaction will be applied to your down payment.
            </div>
          )}
          {!data?.down_payment_sent && !emdDone && (
            <div style={{ marginTop: 12 }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#000', marginBottom: 6 }}>⚠️ Wire Fraud Warning</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  <strong>Never trust wire instructions sent via email.</strong> Always verify instructions directly within this platform. Your down payment must be sent via wire transfer — Doorly does not support ACH.
                </div>
              </div>
              {/* ── 🚨 THE FIGURE IS NOT READY: DISABLE, DO NOT HIDE ──────────
                  emd_remaining is NULL — not 0 — when the server refuses to
                  compute it (down_payment_pct unset, or a claimed payment
                  Stripe could not confirm). The old `|| 0` coercions read that
                  as "nothing owed" and would have shown a live "I Have Sent the
                  Wire" button over a $0 figure.

                  ⚠️ AND THE STEP STAYS ON SCREEN. Removing it reads as a step
                  that does not apply to this borrower; a disabled control with
                  the reason under it reads as a step that is not ready yet,
                  which is what is true. */}
              {data?.blocked_reason ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button disabled aria-disabled="true"
                      style={{ padding: 13, background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 10, cursor: 'not-allowed', fontWeight: 700, fontSize: 13 }}>
                      🏛️ View Wire Instructions
                    </button>
                    <button disabled aria-disabled="true"
                      style={{ padding: 13, background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 10, cursor: 'not-allowed', fontWeight: 800, fontSize: 13 }}>
                      ✓ I Have Sent the Wire
                    </button>
                  </div>
                  <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600, lineHeight: 1.6 }}>
                    ⏳ {data.blocked_reason} Nothing is owed yet and nothing has been missed.
                  </div>
                </div>
              ) : data?.down_payment_credit_available && Number(data?.down_payment_credit_amount || 0) >= Number(data?.emd_remaining || 0) && Number(data?.emd_remaining || 0) > 0 ? (
                <button onClick={() => act('apply-credit')} disabled={acting}
                  style={{ width: '100%', padding: 13, background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                  {acting ? 'Confirming…' : '✓ Confirm Credit Application — No Wire Required'}
                </button>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button onClick={() => setShowWireEMD(true)}
                    style={{ padding: 13, background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    🏛️ View Wire Instructions
                  </button>
                  <button onClick={() => act('emd')} disabled={acting}
                    style={{ padding: 13, background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                    {acting ? 'Confirming…' : '✓ I Have Sent the Wire'}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Sent but admin hasn't yet verified */}
          {(data?.down_payment_sent || emdDone) && !data?.down_payment_verified && (
            <div style={{ marginTop: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
              ⏳ Sent — awaiting confirmation from Doorly
            </div>
          )}
          {/* Admin-verified — green confirmation. The verified wire also
              renders as a row in the global Payment History section at
              the bottom of the dashboard. */}
          {data?.down_payment_verified && (
            <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 700 }}>
              ✓ Confirmed received by Doorly
              {data?.down_payment_amount_sent ? ` — $${Number(data.down_payment_amount_sent).toLocaleString()}` : ''}
            </div>
          )}
        </div>
      ),
    },
    // 2. Loan Estimate (LE) checklist item REMOVED — the LE is now handled
    //    on Screen 1 of the offer-accepted review overlay (HeroOfferAccepted),
    //    so by the time the borrower lands in option_period the LE has
    //    already been viewed and timestamped on users.le_viewed_at.
    //
    // 2b. Purchase Contract (PSA) — in-app canvas signing flow.
    //     Primary + co-borrower (if any) both sign in one session via
    //     PSASigningModal. Doorly countersigns afterwards via a discrete
    //     single-signer BoldSign envelope; the package status reflects
    //     that countersign envelope, not the borrower-side signing.
    {
      id: 'psa-checklist-item',
      done: op.psa_package_status === 'completed',
      actionable: !!op.psa_package_status,
      label: 'Sign Purchase Contract (PSA)',
      description: 'Your Purchase and Sale Agreement with Doorly. Sign to confirm the terms of your home purchase.',
      content: (() => {
        const pkgStatus = op.psa_package_status || null;
        const primarySigned = !!op.psa_signed_primary_at;
        const coSigned      = !!op.psa_signed_coborrower_at;
        const fullySigned   = pkgStatus === 'completed';
        const declined      = pkgStatus === 'declined';
        const expired       = pkgStatus === 'expired';
        const revoked       = pkgStatus === 'revoked';
        const hasCoBorrower = !!isCoBorrower || !!op.psa_signed_coborrower_at; // best-effort flag from dashboard scope

        // State 4: Fully executed — green card matching the Down Payment
        // Received card so the two "done" states on the dashboard read
        // consistently.
        if (fullySigned) {
          return (
            <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 700 }}>
              ✓ Your Purchase Contract has been fully executed and can be viewed on your Documents page under "Property Documents".
            </div>
          );
        }

        // Terminal failure states
        if (declined || expired || revoked) {
          const label = declined ? 'declined' : expired ? 'expired' : 'revoked';
          return (
            <div style={{ marginTop: 8, color: '#374151', fontWeight: 600, fontSize: 13 }}>
              ⚠ Purchase Contract signing was {label}. Doorly is reaching out to re-send. Contact 512-713-6016 if urgent.
            </div>
          );
        }

        // State 3: Both borrower signatures done, waiting on Jesse to
        // countersign. The in-app countersign flow sets
        // pkgStatus='awaiting_doorly_countersign'; the legacy BoldSign
        // envelope used 'sent' — both are accepted so historical rows
        // still resolve to this state.
        if (primarySigned && (!hasCoBorrower || coSigned) &&
            (pkgStatus === 'awaiting_doorly_countersign' || pkgStatus === 'sent')) {
          return (
            <div style={{ marginTop: 8, color: '#374151', fontWeight: 600, fontSize: 13 }}>
              ✓ Your Purchase Contract has now been routed to Doorly to countersign.
            </div>
          );
        }

        // Primary signed, co-borrower hasn't — the requesting user sees the
        // CTA if they're the co-borrower, otherwise a waiting note.
        if (primarySigned && hasCoBorrower && !coSigned) {
          if (isCoBorrower) {
            return (
              <div style={{ marginTop: 8 }}>
                <button onClick={() => setPsaModalOpen(true)}
                  style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Review & Sign →
                </button>
              </div>
            );
          }
          return (
            <div style={{ marginTop: 8, color: '#2d6a0a', fontWeight: 600, fontSize: 13 }}>
              ✓ Your signature is complete. Waiting for co-borrower to sign.
            </div>
          );
        }

        // Co-borrower signed first, primary hasn't — primary sees the CTA
        // plus a note that the co-borrower already signed. Co-borrower sees
        // a waiting note. Independent-signing flow makes this state real.
        if (!primarySigned && hasCoBorrower && coSigned) {
          if (isCoBorrower) {
            return (
              <div style={{ marginTop: 8, color: '#2d6a0a', fontWeight: 600, fontSize: 13 }}>
                ✓ Your signature is complete. Waiting for primary borrower to sign.
              </div>
            );
          }
          return (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setPsaModalOpen(true)}
                style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review & Sign →
              </button>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                ✓ Your co-borrower has already signed — your signature completes the contract.
              </div>
            </div>
          );
        }

        // Neither borrower has signed — open the modal.
        if (!primarySigned && !coSigned) {
          return (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setPsaModalOpen(true)}
                style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review & Sign →
              </button>
            </div>
          );
        }

        // Fall-through. Two cases land here:
        //   (a) PSA is generated (admin hit step 7 → pkgStatus='sent') but
        //       both signatures are still null AND the borrower viewing
        //       this dashboard is in some edge state the earlier
        //       conditions didn't cover. The borrower should still be
        //       able to sign — surface the CTA.
        //   (b) PSA has not been generated yet (pkgStatus is null and the
        //       borrower_psa document doesn't exist). Borrower waits.
        if (pkgStatus === 'sent' || pkgStatus === 'awaiting_doorly_countersign') {
          return (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setPsaModalOpen(true)}
                style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review & Sign →
              </button>
            </div>
          );
        }
        return (
          <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginTop: 8 }}>
            Your Purchase Contract is being prepared — you'll be able to sign here once it's ready.
          </p>
        );
      })(),
    },
    // 3. Appraisal Package — progressive status (no longer a manual
    //    "Order" CTA; the package auto-orders the moment Accounting
    //    verifies the down-payment wire in DoorlyDesk).
    (() => {
      const apprStatus = (op.appraisal_status || '').toLowerCase();
      let badge, badgeBg, badgeColor, message, doneState;
      if (!op.inspection_appraisal_ordered) {
        badge = 'Pending'; badgeBg = '#f3f4f6'; badgeColor = '#6b7280';
        message = 'Doorly to order. Pending receipt of down payment wire and executed contract.';
        doneState = false;
      } else if (apprStatus === 'complete' || op.appraisal_report_text) {
        badge = 'Complete'; badgeBg = '#f0fdf4'; badgeColor = '#166534';
        message = op.appraisal_report_text || 'Your appraisal report is ready. Review it below.';
        doneState = true;
      } else if (apprStatus === 'scheduled' || op.appraisal_scheduled_at) {
        badge = 'Scheduled'; badgeBg = '#f3f4f6'; badgeColor = '#374151';
        const when = op.appraisal_scheduled_at
          ? new Date(op.appraisal_scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;
        message = when
          ? `Appraisal scheduled for ${when}. Reports will be available here once complete.`
          : 'Your appraisal has been scheduled. Reports will be available here once complete.';
        doneState = false;
      } else {
        badge = 'Order Placed'; badgeBg = '#eff6ff'; badgeColor = '#1d4ed8';
        message = "Order placed. We're scheduling your appraisal — you'll be notified when your report is ready.";
        doneState = false;
      }
      return {
        id: 'inspection-appraisal-checklist-item',
        done: doneState,
        actionable: false,   // Doorly orders it
        label: 'Order Appraisal Package',
        description: message,
        content: (
          <div style={{ marginTop: 12 }}>
            <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 9999, background: badgeBg, color: badgeColor, fontSize: 12, fontWeight: 700 }}>
              {badge}
            </span>
          </div>
        ),
      };
    })(),
    // 3.5 Independent inspection notice — slots in right after the
    // Appraisal Package card. Standalone amber notice (no checkbox
    // semantic) so it reads as a tip, not a task. Rendered specially
    // by the .map below — kind:'notice' skips the checkbox column
    // and lets the content span full width.
    {
      id: 'independent-inspection-notice',
      kind: 'notice',
      content: (
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '16px 20px',
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#000', marginBottom: 6 }}>
            🔍 Independent Home Inspection
          </div>
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
            You are welcome to obtain your own independent home inspection
            at your expense. It must be completed within the option period.
            Any seller credit requests based on your inspection findings
            must be received no later than{' '}
            <strong>2 days before the option period deadline</strong>
            {op?.option_end_date
              ? ` (by ${new Date(
                  new Date(String(op.option_end_date).slice(0, 10) + 'T12:00:00').getTime() - 2 * 24 * 60 * 60 * 1000
                ).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})`
              : ''}.
          </p>
        </div>
      ),
    },
    // 4. Review Reports
    {
      id: 'reports-checklist-item',
      done: op.report_review_status === 'complete',
      actionable: !!(op.appraisal_status === 'complete' || op.appraisal_report_text),
      label: 'Review Appraisal Package',
      description: inspAppraisalOrdered
        ? 'Review your findings. Use Doorly Analytics for an AI-powered summary and seller credit recommendation.'
        : 'Reports will be available once your appraisal package is ordered.',
      content: (
        <div style={{ marginTop: '12px' }}>
          {(op.appraisal_status === 'complete' || op.appraisal_report_text) ? (
            <button onClick={() => setShowInspectionModal(true)}
              style={{ padding: '10px 20px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
              Review Report & AI Analysis →
            </button>
          ) : (
            <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 9999, background: '#f3f4f6', color: '#6b7280', fontSize: 12, fontWeight: 700 }}>
              Pending
            </span>
          )}
        </div>
      ),
    },
    // 5. Decision
    {
      id: 'decision-checklist-item',
      done: decisionDone,
      label: decisionDone ? (tasks.decision === 'move_forward' ? 'Decision: Moving Forward 🎉' : 'Decision: Terminating Contract') : 'Confirm Move Forward Decision',
      description: decisionDone ? 'Your loan team has been notified.' : "Once you've reviewed everything, confirm you're ready to proceed to closing.",
      content: !decisionDone ? (
        <div className="mt-3 space-y-2">
          {/* Wrapper card around the move-forward radio so the click
              target reads as a deliberate, primary action — the bare
              <label>+radio pair was easy to miss. Border color +
              background shift on selection give visual confirmation. */}
          <label
            onClick={() => setDecisionChoice('move_forward')}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '14px 16px',
              border: `2px solid ${decisionChoice === 'move_forward' ? '#3a6ad9' : '#e5e7eb'}`,
              borderRadius: 12,
              background: decisionChoice === 'move_forward' ? '#eff6ff' : '#fff',
              cursor: 'pointer',
              transition: 'border-color 0.12s, background 0.12s',
            }}>
            <input type="radio" name="op_decision" value="move_forward"
              checked={decisionChoice === 'move_forward'}
              onChange={() => setDecisionChoice('move_forward')}
              className="accent-[#3a6ad9]"
              style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                Move forward — I'm ready to close
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                Click to select, then confirm below.
              </div>
            </div>
          </label>
          {decisionChoice && (
            <button onClick={() => act('decision', { decision: decisionChoice })} disabled={acting}
              className={`mt-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-colors ${decisionChoice === 'terminate' ? 'bg-red-600 text-white hover:bg-red-700' : 'text-white'} disabled:opacity-50`}
              style={decisionChoice !== 'terminate' ? { backgroundColor: '#3a6ad9', color: '#fff' } : {}}>
              {acting ? 'Submitting…' : decisionChoice === 'terminate' ? 'Confirm Termination' : "Yes, I'm Ready to Close! 🏠"}
            </button>
          )}
        </div>
      ) : null,
    },
  ].map((item, i, arr) => {
    // 🔑 THE COLOUR RULE (2026-11-02): white rows; the ONE item waiting on the borrower in the light-blue panel; a green
    // check when done; red only when overdue.
    const waiting = i === waitingIndex(arr);
    const colours = checklistItemColours({ done: item.done, overdue: !!item.overdue });
    const borderStyle = waiting
      ? { ...CHECK_COLOURS.waiting, borderRadius: 10, padding: '10px 12px', marginBottom: '18px' }
      : { borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none', paddingBottom: '18px', marginBottom: '18px' };
    // kind:'notice' renders full-width content with no checkbox
    // column or label/description scaffold. Used for the
    // Independent Inspection notice that sits between the
    // Appraisal Package and Review Reports cards.
    if (item.kind === 'notice') {
      return (
        <div key={item.id || i} id={item.id} style={borderStyle}>
          {item.content}
        </div>
      );
    }
    return (
      <div key={item.id || i} id={item.id} data-testid="checklist-item" data-waiting={waiting ? 'true' : 'false'} data-done={item.done ? 'true' : 'false'} style={borderStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
            background: colours.circleBg,
            border: `2px solid ${colours.circleBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: '800', color: colours.tick || '#000',
          }}>
            {item.done ? '✓' : ''}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: item.done ? '#9ca3af' : '#111827', textDecoration: item.done ? 'line-through' : 'none' }}>
                {item.label}
              </span>
              {item.urgent && !item.done && item.urgentLabel && (
                <span data-testid="checklist-due" data-overdue={item.overdue ? 'true' : 'false'} style={{ fontSize: '11px', background: '#fff', border: `1px solid ${item.overdue ? CHECK_COLOURS.overdue : '#e5e7eb'}`, color: colours.label || '#374151', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', flexShrink: 0 }}>
                  {item.urgentLabel}
                </span>
              )}
            </div>
            <p style={{ margin: '0', fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>{item.description}</p>
            {item.content}
          </div>
        </div>
      </div>
    );
  })}
  </div>

  {/* ── Key Deadlines ── */}
  {(kd.option_period_end || kd.emd_due_date) && (
  <div className="bg-white rounded-xl border border-gray-100 p-5">
  <p className="text-sm font-bold text-gray-900 mb-4">⚠️ Key Deadlines</p>
  <div className="space-y-3">
  {[
    { label: 'Option Period Ends', date: kd.option_period_end },
    { label: 'Down Payment Due', date: kd.emd_due_date },
  ].map(({ label, date }) => {
    if (!date) return null;
    // CST-safe: parse date string as local date to prevent timezone shifting
    const raw = String(date);
    const dateStr = raw.length >= 10 ? raw.slice(0, 10) : raw;
    const [yr, mo, dy] = dateStr.split('-').map(Number);
    const d = new Date(yr, mo - 1, dy);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const daysLeft = Math.ceil((d - today) / 86400000);
    // 🔑 The colour rule (2026-11-02): red only once it has passed; otherwise neutral.
    const color = daysLeft <= 0 ? CHECK_COLOURS.overdue : '#374151';
    const bg    = '#fff';
    return (
      <div key={label} className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <span className="flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: bg, color }}>
          {daysLeft <= 0 ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
        </span>
      </div>
    );
  })}
  </div>
  </div>
  )}

  {/* ── Support Panel ── */}
  <div className="rounded-xl p-5" data-testid="op-here-to-help" style={{ ...CHECK_COLOURS.card }}>
  <p className="text-sm font-bold mb-2" style={{ color: '#000' }}>💬 We're Here to Help</p>
  <p className="text-sm leading-relaxed mb-4" style={{ color: '#374151' }}>
    Doorly is coordinating everything behind the scenes. If you have questions about
    sending funds, your appraisal, or next steps, we're here to help.
  </p>
  <div className="flex flex-col sm:flex-row gap-3">
    <a href="/messages"
      className="flex-1 text-center text-sm font-semibold py-2.5 px-4 rounded-lg"
      style={{ backgroundColor: '#3a6ad9', color: '#fff' }}>
      💬 Message Doorly
    </a>
    <a href="tel:+15125550100"
      className="flex-1 text-center text-sm font-semibold py-2.5 px-4 rounded-lg border border-blue-300 bg-white" style={{ color: '#2854c0' }}>
      📞 Call Support
    </a>
  </div>
  </div>

  {/* ── Inspection/AI Analysis Modal ── */}
  {showInspectionModal && (
  <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
  <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '620px', width: '100%', margin: 'auto' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🔍 Reports & Analysis</h3>
      <button onClick={() => setShowInspectionModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6b7280' }}>✕</button>
    </div>
    <div style={{ display: 'flex', gap: 0, marginBottom: '20px', background: '#f3f4f6', borderRadius: '10px', padding: '4px' }}>
      {[{ id: 'review', label: '📄 Reports' }, { id: 'ai', label: '🤖 Doorly Analytics' }].map(tab => (
        <button key={tab.id} onClick={() => setInspectionTab(tab.id)}
          style={{ flex: 1, padding: '9px', background: inspectionTab === tab.id ? '#fff' : 'transparent', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: inspectionTab === tab.id ? '#111827' : '#6b7280', boxShadow: inspectionTab === tab.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
          {tab.label}
        </button>
      ))}
    </div>
    {inspectionTab === 'review' && (
    <div>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-center text-gray-400">
        <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
        <p className="text-sm font-semibold mb-1">Reports</p>
        <p className="text-xs">Appraisal package reports will appear here once uploaded.</p>
      </div>
      <button onClick={() => act('review-complete')} disabled={acting}
        className="w-full mt-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: '#3a6ad9' }}>
        {acting ? 'Saving…' : '✓ Mark Reports as Reviewed'}
      </button>
    </div>
    )}
    {inspectionTab === 'ai' && (
    <div>
      <div className="rounded-lg p-4 mb-4" style={{ background: '#edf1fb', border: '1px solid #ddd6fe' }}>
        <p className="text-xs font-bold mb-1" style={{ color: '#5b21b6' }}>🤖 Doorly Analytics</p>
        <p className="text-xs text-gray-600 leading-relaxed">AI-powered summary of your option period status. Instead of requesting repairs, Doorly requests seller credits — keeping your transaction streamlined.</p>
      </div>
      {!aiSummary && !aiLoading && (
        <button onClick={async () => {
          setAiLoading(true);
          try {
            const r = await axios.get(`${API_URL}/api/option-period/ai-summary`, { headers });
            setAiSummary(r.data.summary);
          } catch (_) { setAiSummary('Unable to generate summary at this time.'); }
          setAiLoading(false);
        }}
        className="w-full py-2.5 rounded-lg text-sm font-bold text-white mb-3" style={{ backgroundColor: '#3a6ad9' }}>
          Generate AI Summary →
        </button>
      )}
      {aiLoading && <div className="text-center py-8 text-gray-400"><div style={{ fontSize: 32, marginBottom: 8 }}>⟳</div>Analyzing...</div>}
      {aiSummary && (
        <div className="rounded-lg bg-gray-50 p-4 mb-4">
          <p className="text-xs font-bold text-gray-700 mb-2">Summary</p>
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{aiSummary}</p>
        </div>
      )}
      {/* Independent home-inspection notice. Borrower may obtain
          their own home inspection at their expense; any seller-credit
          ask based on those findings has a hard 2-day-before-deadline
          cutoff so the option-period close can still be coordinated. */}
      {aiSummary && !creditSent && (() => {
        const optionEnd = op.option_end_date || kd.option_period_end || null;
        const cutoff = optionEnd ? (() => {
          const d = new Date(String(optionEnd).slice(0, 10) + 'T12:00:00');
          d.setDate(d.getDate() - 2);
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        })() : null;
        return (
          <div className="rounded-lg p-4 mb-3" style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: '#000' }}>🔍 Independent Home Inspection</p>
            <p className="text-xs leading-relaxed" style={{ color: '#374151' }}>
              You're welcome to obtain your own independent home inspection at your expense.
              It must be completed within the option period. Any seller-credit request based
              on your inspection findings must be received no later than{' '}
              <strong>2 days before the option period deadline{cutoff ? ` (by ${cutoff})` : ''}</strong>.
            </p>
          </div>
        );
      })()}
      {/* Credit Request */}
      {aiSummary && !creditSent && (
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-bold text-gray-700 mb-3">Submit Seller Credit Request</p>
          <div className="flex gap-2 mb-2 items-center">
            <span className="text-sm text-gray-500">$</span>
            <input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="0"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <textarea value={creditReason} onChange={e => setCreditReason(e.target.value)}
            placeholder="Reason for credit request..."
            rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-vertical mb-3" style={{ fontFamily: 'inherit' }} />
          {/* Optional inspection-report attachment (PDF/JPG/PNG). When
              present, the form uploads it first, then includes the
              resulting documentId on the credit-request POST so the
              admin queue can review the actual report. */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Attach Inspection Report <span className="text-gray-400 font-normal">(optional but recommended)</span>
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => setInspectionReportFile(e.target.files?.[0] || null)}
              className="text-xs"
            />
            {inspectionReportFile && (
              <p className="text-xs text-gray-500 mt-1">{inspectionReportFile.name} selected</p>
            )}
          </div>
          {creditError && (
            <div className="mb-3 px-3 py-2 rounded text-xs" style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {creditError}
            </div>
          )}
          <button onClick={async () => {
            if (!creditAmount || !creditReason || creditSubmitting) return;
            setCreditSubmitting(true);
            setCreditError(null);
            try {
              let inspectionReportDocumentId = null;
              if (inspectionReportFile) {
                const fd = new FormData();
                fd.append('file', inspectionReportFile);
                const upRes = await axios.post(
                  `${API_URL}/api/option-period/upload-inspection-report`,
                  fd,
                  { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                );
                inspectionReportDocumentId = upRes.data?.documentId || null;
              }
              await axios.post(
                `${API_URL}/api/option-period/credit-request`,
                {
                  amount: creditAmount,
                  reason: creditReason,
                  inspectionReportDocumentId,
                  ownInspection: !!inspectionReportFile,
                },
                { headers }
              );
              setCreditSent(true);
            } catch (err) {
              setCreditError(err.response?.data?.error || 'Could not submit credit request. Please try again.');
            } finally {
              setCreditSubmitting(false);
            }
          }} disabled={!creditAmount || !creditReason || creditSubmitting}
          className="w-full py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-40" style={{ backgroundColor: '#3a6ad9' }}>
            {creditSubmitting ? 'Submitting…' : 'Submit to Doorly Contract Desk →'}
          </button>
        </div>
      )}
      {creditSent && (
        <div className="rounded-lg p-4 text-center text-sm font-semibold" style={{ backgroundColor: '#fcfff5', border: '1px solid #bbf7d0', color: '#1a1a1a' }}>
          ✓ Credit request submitted — Doorly's contract desk will coordinate with the seller
        </div>
      )}
    </div>
    )}
  </div>
  </div>
  )}

  {/* Wire Instructions Modal — wireType="down_payment" renders the
      Down Payment copy + DP-* reference. The modal still accepts the
      legacy "emd" wireType for any non-option-period callers. */}
  <WireInstructionsModal
    isOpen={showWireEMD}
    onClose={() => setShowWireEMD(false)}
    wireType="down_payment"
    loanNumber={data?.loan_number || null}
    borrowerName={name || null}
    amount={data?.emd_remaining || null}
    wireData={wire}
    emdBreakdown={data ? {
      borrowerPurchasePrice: data.borrower_purchase_price || 0,
      emdTotal: data.emd_total || 0,
      offerFeeCredit: data.offer_fee_credit || 0,
      emdRemaining: data.emd_remaining || 0,
    } : null}
  />

  {/* Option-Period Withdrawal Modal — 2 steps. Step 1 (warning) makes
      "Cancel — keep my home" the visually prominent CTA so withdrawal
      requires deliberate effort. Step 2 (confirm) gates the destructive
      action behind a checkbox acknowledgment. */}
  {withdrawStep > 0 && (
    <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
         onClick={() => !withdrawSubmitting && setWithdrawStep(0)}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 540, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        {withdrawStep === 1 && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#7a5200' }}>⚠️ Before you continue…</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              Withdrawing during the option period is a significant decision. Please read the following carefully before proceeding.
            </p>
            <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
              <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#7a5200' }}>⚠️ Option Fee Notice</p>
              <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                Doorly paid an option fee to secure your right to inspect this property. If you withdraw now, you may be responsible for reimbursing this fee. The exact amount, if applicable, will be communicated by your Doorly team member.
              </p>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>By withdrawing, you understand:</p>
            <ul style={{ margin: '0 0 18px', paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
              <li>Your option period will end immediately</li>
              <li>You may be charged an option fee — we will confirm the amount</li>
              <li>Your down payment funds will remain in escrow and be applied to your next home purchase through Doorly</li>
              <li>Your loan approval remains active</li>
            </ul>
            {/* Cancel is the PRIMARY visual element here — full width blue,
                vs the "continue" path being a smaller amber outline link. */}
            <button onClick={() => setWithdrawStep(0)}
                    style={{ width: '100%', padding: '12px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
              Cancel — keep my home
            </button>
            <button onClick={() => setWithdrawStep(2)}
                    style={{ width: '100%', padding: '8px 16px', background: 'none', color: '#7a5200', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              I understand — continue
            </button>
          </>
        )}
        {withdrawStep === 2 && (
          <>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Are you sure?</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              This will immediately withdraw you from the option period on{' '}
              <strong>{op.property_address || data?.fallback?.property_address || 'this property'}</strong>. This action cannot be undone.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 18, cursor: 'pointer' }}>
              <input type="checkbox" checked={withdrawAck} onChange={(e) => setWithdrawAck(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                I understand I may be charged an option fee and that my down payment will be applied to a future home purchase
              </span>
            </label>
            <button onClick={async () => {
              setWithdrawSubmitting(true);
              try {
                await axios.post(`${API_URL}/api/borrower/withdraw-option-period`, {}, { headers });
                setWithdrawStep(0);
                if (onStageReset) onStageReset();
              } catch (e) { alert(e.response?.data?.error || e.message); }
              setWithdrawSubmitting(false);
            }} disabled={!withdrawAck || withdrawSubmitting}
              style={{ width: '100%', padding: '10px 16px', background: (!withdrawAck || withdrawSubmitting) ? '#fca5a5' : '#ff082f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 800, cursor: (!withdrawAck || withdrawSubmitting) ? 'not-allowed' : 'pointer', marginBottom: 8 }}>
              {withdrawSubmitting ? 'Submitting…' : 'Confirm Withdrawal'}
            </button>
            <button onClick={() => setWithdrawStep(1)} disabled={withdrawSubmitting}
                    style={{ width: '100%', padding: '8px 16px', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Go back</button>
          </>
        )}
      </div>
    </div>
  )}

  <PSASigningModal
    open={psaModalOpen}
    onClose={() => setPsaModalOpen(false)}
    onSigned={() => { setPsaModalOpen(false); load(); }}
    apiUrl={API_URL}
    token={token}
  />

  </div>
  );
}


// ─── UtilityConnectWidget ─────────────────────────────────────────────────────
// Embedded Utility Connect iframe. Loads iframeResizer.min.js once per
// page and mounts the partner's /doorly?use=widget iframe into its
// container div. Pre-API-integration treatment — the stub backend
// endpoint POST /api/users/utility-connect-referral stays in place for
// when we hit the 10/month threshold and switch to the API. Until then,
// the borrower fills out the widget directly and we get the referral
// credit via the iframe submission.
function UtilityConnectWidget() {
  useEffect(() => {
    const isOldIE = navigator.userAgent.indexOf('MSIE') !== -1;
    let cancelled = false;

    const mountIframe = () => {
      if (cancelled) return;
      const container = document.getElementById('utilityconnectMainDivx');
      // Idempotent — bail if we already injected (e.g. component
      // re-mounted on hot-reload, or another instance is also visible).
      if (!container || container.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', 'https://www.utilityconnect.net/doorly?use=widget');
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('width', '100%');
      iframe.setAttribute('style', 'border:none;');
      container.appendChild(iframe);
      if (window.iFrameResize) {
        try {
          window.iFrameResize({
            log: false,
            autoResize: true,
            checkOrigin: false,
            heightCalculationMethod: isOldIE ? 'max' : 'lowestElement',
            warningTimeout: 10000,
          });
        } catch (e) { /* resizer optional — iframe still renders */ }
      }
    };

    // Inject iframeResizer if not already on the page, then mount.
    const existing = document.querySelector('script[src*="iframeResizer"]');
    if (existing) {
      mountIframe();
    } else {
      const s = document.createElement('script');
      s.src = 'https://www.utilityconnect.net/nexus/dist/js/iframeResizer.min.js';
      s.type = 'text/javascript';
      s.onload = mountIframe;
      document.head.appendChild(s);
    }

    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ background: '#ffffff', borderRadius: 10, overflow: 'hidden' }}>
      <div id="utilityconnectMainDivx"></div>
      <div id="utilityconnectHandDivx"></div>
    </div>
  );
}

// ─── UtilityConnectModal ──────────────────────────────────────────────────────
// Lightbox containing the embedded UtilityConnectWidget. Opened from the
// UtilitySetupCard's "Set Up Utilities" CTA on the dashboard and from
// the matching checklist item inside HeroClosing. Keeps the dashboard
// clean — the iframe only loads when the borrower actually clicks
// through, instead of rendering inline on every closing-stage view.
function UtilityConnectModal({ onClose }) {
  // Lock body scroll while the modal is open + restore on close. Same
  // pattern other modals on this page use (PaymentModal etc.).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      onClick={onClose}
      className="safe-modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="safe-modal-box"
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
          '--sm-max': '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px', borderBottom: '1px solid #f3f4f6',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
              ⚡ Set Up Your Utilities
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Electricity, gas, water, and internet — all in one place.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', fontSize: 22, color: '#9ca3af',
              cursor: 'pointer', lineHeight: 1, padding: 4,
            }}>
            ✕
          </button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          <UtilityConnectWidget />
          <div style={{
            marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6',
            fontSize: 12, color: '#6b7280', textAlign: 'center', lineHeight: 1.5,
          }}>
            Want to learn more about our utility-setup partner?{' '}
            <a
              href="https://www.utilityconnect.net/doorly"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3a6ad9', fontWeight: 700, textDecoration: 'none' }}>
              Visit Utility Connect ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── UtilitySetupCard ─────────────────────────────────────────────────────────
// Closing-flow CTA card (clear_to_close / closed / funded). White surface
// with lime left accent, lime "Set Up Utilities" button. Button opens
// the UtilityConnectModal — the embedded iframe widget lives inside the
// modal, not on the dashboard surface, so a dashboard view doesn't pay
// the iframe-load cost unless the borrower clicks through.
function UtilitySetupCard() {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderLeft: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: 20,
        marginTop: 24,
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: '#f0fde4',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 22 }}>⚡</span>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111111' }}>
            Set Up Your Utilities
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Don't forget to connect electricity, gas, water, and internet
            before your move-in date. We'll handle the referrals — it takes
            less than a minute.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            background:   '#3a6ad9',
            color:        '#1a1a1a',
            border:       'none',
            borderRadius: 8,
            padding:      '8px 14px',
            fontSize:     13,
            fontWeight:   700,
            cursor:       'pointer',
            whiteSpace:   'nowrap',
            flexShrink:   0,
          }}>
          Set Up Utilities →
        </button>
      </div>
      {modalOpen && <UtilityConnectModal onClose={() => setModalOpen(false)} />}
    </>
  );
}

function HeroClosed({ name, token, isCoBorrower }) {
  const firstName = name ? name.split(' ')[0] : '';
  const [starRating, setStarRating] = useState(null);
  const [starHover, setStarHover] = useState(null);
  const [starStep, setStarStep] = useState('stars'); // stars|feedback|done
  const [starFeedback, setStarFeedback] = useState('');
  const [starResponseId, setStarResponseId] = useState(null);
  const [starSubmitting, setStarSubmitting] = useState(false);
  const [diaryStory, setDiaryStory] = useState('');
  const [diarySubmitted, setDiarySubmitted] = useState(false);
  const [diarySubmitting, setDiarySubmitting] = useState(false);

  const hdrs = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/auth/doorly-diaries`, { headers: hdrs }).then(r => { if (r.data?.diary) { setDiaryStory(r.data.diary.story || ''); setDiarySubmitted(true); } }).catch(() => {});
    axios.get(`${API_URL}/api/surveys/check/closed_review`, { headers: hdrs }).then(r => { if (!r.data?.should_show) setStarStep('done'); }).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ THE RATING IS SUBMITTED ON THE CLICK, AT EVERY VALUE. It used to be sent
  // only at 4+; a 1-3 advanced straight to the comment box and sent NOTHING until
  // that box was submitted, so a borrower who rated 2 and closed the tab was
  // recorded as never having rated at all. The score is the signal — the comment
  // is an optional elaboration on it — and the surface most likely to produce a
  // low score was the one that threw them away.
  //
  // ⚠️ THE COMMENT AMENDS THAT ROW, IT DOES NOT SUBMIT A SECOND ONE.
  // /api/surveys/submit INSERTs a fresh survey_responses row every call, so
  // posting the comment through it would leave TWO rows for one rating — two
  // rows in every average, and, now that a low rating raises a ticket, two
  // tickets for one piece of feedback. So submit returns its row id and the
  // comment PATCHes that row.
  const handleStar = async (rating) => {
    setStarRating(rating);
    const res = await axios
      .post(`${API_URL}/api/surveys/submit`, { trigger_type: 'closed_review', star_rating: rating }, { headers: hdrs })
      .catch(() => null);
    // Null when the post failed. The comment step then has no row to amend, so
    // it degrades to sending the comment as its own response rather than losing
    // it — a duplicate row beats a dropped complaint.
    setStarResponseId(res?.data?.response_id ?? null);
    setStarStep(rating >= 4 ? 'done' : 'feedback');
  };

  const submitFeedback = async () => {
    setStarSubmitting(true);
    if (starResponseId) {
      await axios.post(`${API_URL}/api/surveys/comment`,
        { response_id: starResponseId, testimonial_text: starFeedback }, { headers: hdrs }).catch(() => {});
    } else {
      await axios.post(`${API_URL}/api/surveys/submit`,
        { trigger_type: 'closed_review', star_rating: starRating, testimonial_text: starFeedback }, { headers: hdrs }).catch(() => {});
    }
    setStarStep('done');
    setStarSubmitting(false);
  };

  return (
  <div className="mb-8 space-y-5">
    {/* Hero */}
    <div style={{ background: 'linear-gradient(135deg, #111827 0%, #1e3a5f 100%)', borderRadius: 20, padding: '40px 32px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      {['#acf44e','#fffd5b','#3a6ad9','#ff082f','#acf44e'].map((c, i) => (
        <div key={i} style={{ position: 'absolute', width: 8, height: 8, borderRadius: '50%', background: c, top: [15,25,10,30,20][i]+'%', left: [8,88,45,15,75][i]+'%', opacity: 0.5 }} />
      ))}
      <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 900, color: '#acf44e', letterSpacing: '-0.5px' }}>
        {firstName ? `Congratulations, ${firstName}!` : 'Congratulations!'}
      </h1>
      <p style={{ margin: 0, fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
        Your loan is closed and your home is yours. Welcome to homeownership.
      </p>
    </div>

    {/* Star Rating */}
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '24px 20px', textAlign: 'center' }}>
      {starStep === 'done' ? (
        starRating >= 4 ? (<>
          <div style={{ fontSize: 28, marginBottom: 6 }}>⭐</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Thanks for the love!</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Your feedback means the world to us.</div>
          <TrustpilotPrompt context="borrower" />
        </>) : starRating ? (<>
          <div style={{ fontSize: 28, marginBottom: 6 }}>💙</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Thank you for your feedback</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Our team has been notified.</div>
        </>) : (<>
          <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7280' }}>Survey completed</div>
        </>)
      ) : starStep === 'stars' ? (<>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>Rate Your Doorly Experience</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>How was your homebuying journey?</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => handleStar(s)} onMouseEnter={() => setStarHover(s)} onMouseLeave={() => setStarHover(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 36, padding: 2, transition: 'transform 0.1s', transform: (starHover || 0) >= s ? 'scale(1.15)' : 'scale(1)', filter: starHover ? (starHover >= s ? 'none' : 'grayscale(1) opacity(0.3)') : 'none' }}>⭐</button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 240, margin: '0 auto', fontSize: 11, color: '#9ca3af' }}><span>Poor</span><span>Excellent</span></div>
      </>) : (<>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 14 }}>
          {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: 24, opacity: starRating >= s ? 1 : 0.2 }}>⭐</span>)}
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 6 }}>We're sorry to hear that.</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>What could we have done better?</div>
        <textarea value={starFeedback} onChange={e => setStarFeedback(e.target.value)} maxLength={1000} rows={3} placeholder="Tell us how we could improve..."
          style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', textAlign: 'left', marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 10 }}>
          {/* ⚠️ WAS "Back", AND IT CANNOT BE ANY MORE. Back cleared starRating and
              returned to the stars — harmless while nothing had been sent, but the
              rating is now recorded on the click, so re-rating would INSERT a
              second response for the same borrower. Skip is the honest control:
              the score is already in, the comment is what is optional. */}
          <button onClick={() => setStarStep('done')} style={{ flex: 1, padding: 11, background: '#f3f4f6', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#374151' }}>Skip</button>
          <button onClick={submitFeedback} disabled={!starFeedback.trim() || starSubmitting}
            style={{ flex: 2, padding: 11, background: !starFeedback.trim() ? '#e5e7eb' : '#3a6ad9', color: !starFeedback.trim() ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}>
            {starSubmitting ? 'Submitting...' : 'Submit Feedback →'}
          </button>
        </div>
      </>)}
    </div>

    {/* Doorly Diaries — primary borrower only (one story per loan file) */}
    {!isCoBorrower && (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 24 }}>📖</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#acf44e', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Doorly Diaries</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>Share Your Story</div>
        </div>
      </div>
      {diarySubmitted ? (
        <div style={{ background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>Story submitted — thank you! 🏡</span>
          <button onClick={() => setDiarySubmitted(false)} style={{ marginLeft: 'auto', fontSize: 12, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, lineHeight: 1.6 }}>Tell us about your homebuying journey — you may be featured on Doorly.</p>
          <textarea value={diaryStory} onChange={e => setDiaryStory(e.target.value)} maxLength={500} rows={3}
            placeholder="What did it mean to you? Any advice for others?"
            style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{diaryStory.length}/500</span>
            <button onClick={async () => { if (!diaryStory.trim()) return; setDiarySubmitting(true); await axios.post(`${API_URL}/api/auth/doorly-diaries`, { story: diaryStory.trim() }, { headers: hdrs }).catch(() => {}); setDiarySubmitted(true); setDiarySubmitting(false); }}
              disabled={diarySubmitting || !diaryStory.trim()}
              style={{ padding: '10px 20px', background: !diaryStory.trim() ? '#e5e7eb' : '#acf44e', color: !diaryStory.trim() ? '#9ca3af' : '#1a1a1a', border: 'none', borderRadius: 10, cursor: !diaryStory.trim() ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 14 }}>
              {diarySubmitting ? 'Submitting...' : '🏡 Submit Story'}
            </button>
          </div>
        </div>
      )}
    </div>
    )}

  </div>
  );
}

function HeroOfferSubmitted({ name }) {
  const firstName = (name || '').split(' ')[0] || null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 mb-8">
      <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
        Offer Submitted
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">
        {firstName ? `${firstName}, your offer has been submitted` : 'Your offer has been submitted'}
      </h1>
      <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 12, padding: 24, marginTop: 16 }}>
        <h3 style={{ color: '#b45309', margin: '0 0 8px 0', fontSize: 16, fontWeight: 700 }}>
          Your Offer Is In Review
        </h3>
        <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          Your offer has been submitted to the listing agent. Our team is on it. We'll
          notify you as soon as we hear back.
        </p>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
          Questions? Call us at 512-713-6016 or email contracts@godoorly.com
        </p>
      </div>

      {/* Divider + Doorly negotiation philosophy + what's next */}
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '24px 0 0' }} />
      <div style={{ marginTop: 24 }}>
        <h3 style={{ color: '#111827', margin: '0 0 8px 0', fontSize: 16, fontWeight: 700 }}>
          We negotiate for you — not against you
        </h3>
        <p style={{ margin: '0 0 8px 0', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          At Doorly, our goal is simple: help you win the home at the best possible terms, not just the highest price.
        </p>
        <p style={{ margin: '0 0 12px 0', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          That means every offer we submit is:
        </p>
        <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          <li style={{ marginBottom: 8 }}>
            <strong style={{ color: '#111827' }}>Competitive enough to win</strong> — We position our offers to stand out and get accepted in real market conditions, especially as all cash offers with fast closings.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong style={{ color: '#111827' }}>Strategic, not inflated</strong> — We don't overpay just to get a deal done. Our pricing is grounded in real market data and comparable sales.
          </li>
          <li style={{ marginBottom: 0 }}>
            <strong style={{ color: '#111827' }}>Built to appraise</strong> — We structure offers with the appraisal in mind — helping avoid surprises that could delay closing or require additional cash from you.
          </li>
        </ul>
        <h3 style={{ color: '#111827', margin: '0 0 8px 0', fontSize: 16, fontWeight: 700 }}>
          What to expect next
        </h3>
        <p style={{ margin: 0, color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
          We'll keep you updated as we hear back on your offer. If adjustments are needed to stay competitive, we'll guide you through your options.
        </p>
      </div>
    </div>
  );
}

// ─── PropertyPurchaseSummary — sibling card under HeroOfferAccepted ──────────
// Renders the address + key purchase figures from the
// /api/borrower/offer-accepted-review payload (property_address,
// purchase_price, loan_amount, down_payment, closing_date, contract_date,
// interest_rate, monthly_payment, cash_to_close). Each field falls back
// to an em-dash when the backend hasn't populated it yet (e.g. before
// the contract date is stamped), so partial data still renders a
// coherent card instead of a wall of "$0"s.
function PropertyPurchaseSummary({ state }) {
  if (!state || state.loading) return null;
  const fmt$ = (n) =>
    n != null && Number.isFinite(Number(n))
      ? '$' + Math.round(Number(n)).toLocaleString('en-US')
      : '—';
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      // Backend DATE columns serialize as "YYYY-MM-DD" or as the ISO
      // "YYYY-MM-DDT00:00:00.000Z" (node-postgres pins them to midnight
      // UTC). Either form, fed straight to new Date(), renders the
      // previous day in any timezone west of UTC because JS treats the
      // bare YYYY-MM-DD as UTC midnight. Parse the YYYY-MM-DD prefix
      // and construct a local Date so "2026-06-09" stays June 9 in CDT.
      const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const local = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
      return local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) { return '—'; }
  };
  const fmtPct = (n) =>
    n != null && Number.isFinite(Number(n)) ? Number(n).toFixed(3) + '%' : '—';

  const rows = [
    ['Purchase Price', fmt$(state.purchase_price)],
    ['Loan Amount',    fmt$(state.loan_amount)],
    ['Down Payment',   fmt$(state.down_payment)],
    ['Monthly Payment', fmt$(state.monthly_payment)],
    ['Cash to Close', fmt$(state.cash_to_close)],
    ['Interest Rate', fmtPct(state.interest_rate)],
    ['Contract Date', fmtDate(state.contract_date)],
    ['Closing Date',  fmtDate(state.closing_date)],
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6 mb-8">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Your purchase</span>
      </div>
      {state.property_address && (
        <p className="text-base sm:text-lg font-bold text-black mb-4 leading-snug">
          {state.property_address}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
            <div className="text-sm font-bold text-black mt-0.5">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── HeroOfferAccepted — offer_accepted stage hero ───────────────────────────
// Renders when loan_stage === 'offer_accepted' (after the listing agent
// accepts the offer in /offer-review). Three states keyed off the
// borrower's progress through the 5-screen overlay:
//   - not started:   "Your offer has been accepted 🎉" + Review & Continue
//   - in progress:   "Continue your review" + Continue Review
//   - completed:     "Review complete — waiting for contract execution"
// The review-complete state lasts until Jesse signs the A-B PSA in
// BoldSign and the webhook flips the stage to option_period.
function HeroOfferAccepted({ name, token, isCoBorrower, onStageReset }) {
  const [state, setState] = useState({ loading: true });
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showCoReview, setShowCoReview] = useState(false);   // 🔑 the co-borrower's screen 1 (Part 1 item 7)
  // Decline modal — borrower elects not to proceed at offer_accepted.
  // Two-step: pick reason, then confirm. Posts to /api/borrower/decline-offer-accepted
  // which resets the loan to home_shopping and waives the next offer fee.
  const [declineStep, setDeclineStep] = useState(0); // 0 = closed, 1 = reason, 2 = confirm
  const [declineReason, setDeclineReason] = useState('');
  const [declineDetail, setDeclineDetail] = useState('');
  const [declineSubmitting, setDeclineSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = () => {
    if (!token) return;
    axios.get(`${API_URL}/api/borrower/offer-accepted-review`, { headers })
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false }));
  };
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 CO-BORROWER (Part 1 item 7, Jesse 2026-10-25): his own Loan Estimate — he reviews it and confirms his intent to
  // proceed (screen 1 of the review, after his own E-Sign consent). The rest of the review stays the primary's.
  if (isCoBorrower && !state.loading && !(state.le_intent_to_proceed_at || state.intent_to_proceed_at)) {
    return (
      <>
        <div data-testid="coborrower-le-ready" className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-200 shadow-sm p-5 sm:p-8 mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">Your offer has been accepted 🎉</h1>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Your Loan Estimate is ready. Please review it and confirm that you intend to proceed.
            {state.primary_first_name ? ` ${state.primary_first_name} completes the rest of the review.` : ''}
          </p>
          <button type="button" data-testid="coborrower-le-open" onClick={() => setShowCoReview(true)}
            className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold" style={{ background: '#3a6ad9' }}>
            Review your Loan Estimate
          </button>
        </div>
        {showCoReview && (
          <OfferAcceptedReview token={token} onClose={() => { setShowCoReview(false); refresh(); }}
            onComplete={() => { setShowCoReview(false); refresh(); }} />
        )}
        <PropertyPurchaseSummary state={state} />
      </>
    );
  }
  if (isCoBorrower) {
    return (
      <>
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-200 shadow-sm p-5 sm:p-8 mb-4">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
            ⏱ Time-sensitive
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">Your offer has been accepted 🎉</h1>
          <p className="text-gray-600 text-base leading-relaxed">
            The primary borrower is reviewing the loan terms and confirming next steps.
            You'll be notified as soon as the loan moves into the option period.
          </p>
        </div>
        <PropertyPurchaseSummary state={state} />
      </>
    );
  }

  if (state.loading) {
    return <div className="mb-8 h-32 bg-gray-100 rounded-2xl animate-pulse" />;
  }

  const completed = !!state.offer_review_completed_at;
  const currentStep = Number(state.current_step) || 1;
  const inProgress = !completed && (currentStep > 1 || !!state.le_viewed_at || !!state.intent_to_proceed_at);

  // Post-completion (waiting on Jesse's signature)
  if (completed) {
    return (
      <>
        <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl border border-green-200 shadow-sm p-5 sm:p-8 mb-4">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
            ✅ Review Complete
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">Waiting for contract execution</h1>
          <p className="text-gray-600 text-base leading-relaxed">
            We're finalizing the purchase contract. You'll be notified when we're officially under contract.
          </p>
        </div>
        <PropertyPurchaseSummary state={state} />
      </>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-200 shadow-sm p-5 sm:p-8 mb-4">
        <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
          ⏱ Time-sensitive
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-black mb-3">
          {inProgress ? 'Continue your review' : 'Your offer has been accepted 🎉'}
        </h1>
        <p className="text-gray-600 text-base leading-relaxed mb-2">
          {inProgress
            ? 'Your offer has been accepted. Confirm your next step within 24 hours to keep your home under contract.'
            : 'Review your loan terms and confirm your next step to keep your home under contract.'}
        </p>
        {!inProgress && (
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            This step takes about 2–3 minutes and is required to move forward.
          </p>
        )}
        <button
          type="button" onClick={() => setOverlayOpen(true)}
          className="w-full sm:w-auto h-12 px-6 rounded-xl text-sm font-bold text-white inline-flex items-center justify-center gap-2"
          style={{ backgroundColor: '#3a6ad9' }}
        >
          {inProgress ? 'Continue Review' : 'Review & Continue'}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
        </button>
        <button type="button" onClick={() => { /* dismiss with no state change */ }}
          className="w-full sm:w-auto sm:ml-3 h-12 px-4 mt-2 sm:mt-0 text-sm text-gray-500"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          Save for later
        </button>
        {/* Decline link — small, gray, understated. Last-resort path that
            opens the 2-step decline modal. */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
          <button type="button"
            onClick={() => { setDeclineStep(1); setDeclineReason(''); setDeclineDetail(''); }}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
            I do not want to proceed with this home
          </button>
        </div>
      </div>
      <PropertyPurchaseSummary state={state} />
      {overlayOpen && (
        <OfferAcceptedReview
          token={token}
          borrowerName={name}
          onClose={() => { setOverlayOpen(false); refresh(); }}
          onComplete={() => { setOverlayOpen(false); refresh(); }}
        />
      )}
      {declineStep > 0 && (
        <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={() => !declineSubmitting && setDeclineStep(0)}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 500, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            {declineStep === 1 && (
              <>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Are you sure you want to decline?</h3>
                <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                  Please let us know why you'd like to step back from this home.
                </p>
                <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}>
                  <option value="">Select a reason…</option>
                  <option value="The home isn't right for me anymore">The home isn't right for me anymore</option>
                  <option value="I found a different home I prefer">I found a different home I prefer</option>
                  <option value="The offer price / terms don't work for me">The offer price / terms don't work for me</option>
                  <option value="I need more time — I'm not ready">I need more time — I'm not ready</option>
                  <option value="Other">Other</option>
                </select>
                {declineReason === 'Other' && (
                  <textarea value={declineDetail} onChange={(e) => setDeclineDetail(e.target.value)} rows={3} placeholder="Please describe…"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }} />
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                  <button onClick={() => setDeclineStep(0)}
                          style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => setDeclineStep(2)} disabled={!declineReason || (declineReason === 'Other' && !declineDetail.trim())}
                          style={{ padding: '8px 16px', background: (!declineReason || (declineReason === 'Other' && !declineDetail.trim())) ? '#cbd5e1' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (!declineReason || (declineReason === 'Other' && !declineDetail.trim())) ? 'not-allowed' : 'pointer' }}>Next →</button>
                </div>
              </>
            )}
            {declineStep === 2 && (
              <>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Confirm your decision</h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  By declining, you are choosing not to move forward with this home. Here's what happens next:
                </p>
                <ul style={{ margin: '0 0 18px', paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                  <li>Your loan file will be reset to Home Shopping</li>
                  <li>You can browse and select a new home at any time</li>
                  <li>No new offer request fee will be required for your next offer</li>
                </ul>
                <button onClick={async () => {
                  setDeclineSubmitting(true);
                  try {
                    await axios.post(`${API_URL}/api/borrower/decline-offer-accepted`,
                      { reason: declineReason, reasonDetail: declineReason === 'Other' ? declineDetail : null },
                      { headers });
                    setDeclineStep(0);
                    if (onStageReset) onStageReset();
                  } catch (e) { alert(e.response?.data?.error || e.message); }
                  setDeclineSubmitting(false);
                }} disabled={declineSubmitting}
                  style={{ width: '100%', padding: '10px 16px', background: '#fff', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: declineSubmitting ? 'wait' : 'pointer', marginBottom: 8 }}>
                  {declineSubmitting ? 'Submitting…' : "Yes, I'm done with this home"}
                </button>
                <button onClick={() => setDeclineStep(1)} disabled={declineSubmitting}
                        style={{ width: '100%', padding: '8px 16px', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Go back</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── HeroClosing — Closing-stage hero ─────────────────────────────────────────
// Mirrors the HeroOptionPeriod checklist pattern. Renders for stage in
// ['closing', 'clear_to_close']. Items: Sign CD (DocMagic), Upload HOI
// Declaration, Send Cash to Close (wire), Begin E-Closing.
//
// The CD sign step uses the existing DocMagic SSO flow (no in-page pad).
// HOI upload reuses /api/documents/upload with document_type=hoi_declaration.
// CTC wire reuses the existing closing_disclosures.wire_sent column via
// POST /api/closing/wire-sent. E-closing gates on docmagic_documents
// signed-at + a 4-business-day wait, then either opens the admin-set link
// or fires a request flag for admin to follow up.
function HeroClosing({ name, token, isCoBorrower, primaryName }) {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showWireCTC, setShowWireCTC] = useState(false);
  const [utilityModalOpen, setUtilityModalOpen] = useState(false);
  // HOI modal — two-option surface (shop via partner / upload existing
  // dec page). Replaces the prior inline upload-only button.
  const [showHoiModal, setShowHoiModal] = useState(false);
  const [showHoiInstr, setShowHoiInstr] = useState(false);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    if (!token) return Promise.resolve();
    return axios.get(`${API_URL}/api/closing/status`, { headers })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark the "Set Up Your Utilities" step complete when the embedded
  // UtilityConnect widget reports a submission. The widget is a cross-origin
  // iframe (utilityconnect.net), so a postMessage from that origin is our only
  // completion signal — we ignore the iframeResizer sizing chatter and treat a
  // submit/complete/thank-you message as done. Idempotent via utilDoneRef.
  const utilDoneRef = useRef(false);
  useEffect(() => {
    const markUtilitiesComplete = async () => {
      if (utilDoneRef.current) return;
      utilDoneRef.current = true;
      try {
        await axios.post(`${API_URL}/api/closing/utilities-complete`, {}, { headers });
        await load();
      } catch (_) { utilDoneRef.current = false; }
    };
    const SUBMIT_RE = /submit|complete|success|thank|confirmation|\blead\b/i;
    const onMsg = (e) => {
      if (e.origin !== 'https://www.utilityconnect.net') return;
      const data = e.data;
      if (typeof data === 'string') {
        if (data.startsWith('[iFrameSizer]')) return; // resize chatter — not a submission
        if (SUBMIT_RE.test(data)) markUtilitiesComplete();
        return;
      }
      if (data && typeof data === 'object') {
        const fields = [data.type, data.event, data.action, data.status, data.name, data.message]
          .filter(Boolean).map(String).join(' ');
        if (SUBMIT_RE.test(fields) || SUBMIT_RE.test(JSON.stringify(data))) markUtilitiesComplete();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="mb-8 space-y-4 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-2xl" />
        <div className="h-48 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  const d = data || {};
  const fmt$ = (v) => (v == null || v === '' ? null : '$' + Number(v).toLocaleString());
  const shortDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  // Per-item completion booleans. CD considered "this borrower's" signed
  // status (cd_signed = primary_signed_at; cd_coborrower_signed for the
  // co-side). Determine which side the viewer is on, then pick that flag.
  const myCdSigned    = d.is_coborrower ? !!d.cd_coborrower_signed : !!d.cd_signed;
  const myCdSignedAt  = d.is_coborrower ? d.cd_coborrower_signed_at : d.cd_signed_at;
  const otherCdSigned = d.is_coborrower ? !!d.cd_signed : !!d.cd_coborrower_signed;
  const hoiDone   = !!d.hoi_uploaded;
  const ctcSent   = !!d.ctc_wire_sent;
  const ctcVerified = !!d.ctc_wire_verified;
  const coName = d.coborrower_name || null;

  // ── Action handlers ────────────────────────────────────────────────────────
  // Route to the in-app CD signing flow on /portal/closing-disclosures.
  // The old DocMagic SSO route ('GET /api/docmagic/sign-url/:userId/cd')
  // is gone — that endpoint still throws "Package not yet created" when
  // DocMagic hasn't produced a package_id, which is the wrong failure
  // mode now that CD signing is a native canvas flow.
  const handleSignCd = () => {
    navigate('/portal/closing-disclosures');
  };

  const handleHoiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setActing(true); setErrorMsg('');
    const fd = new FormData();
    fd.append('files', file);
    fd.append('document_type', 'hoi_declaration');
    try {
      await axios.post(`${API_URL}/api/documents/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      // Refresh closing status BEFORE closing the modal so the dashboard
      // reflects the upload the moment the popup closes (no manual refresh).
      await load();
      setShowHoiModal(false);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setActing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCtcSent = async () => {
    setActing(true); setErrorMsg('');
    try {
      // Send the *net* amount (CTC minus the verified option-period down
      // payment) so the closing_disclosures.cash_to_close_amount column
      // matches what the borrower actually wired — falls back to the
      // gross/legacy fields when net isn't computed yet.
      const amountWired = d.net_cash_to_close ?? d.cd_cash_to_close ?? d.cash_to_close_amount ?? null;
      await axios.post(`${API_URL}/api/closing/wire-sent`, { amount: amountWired }, { headers });
      load();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Could not mark wire as sent.');
    } finally {
      setActing(false);
    }
  };

  // ── Co-borrower status indicator helper ────────────────────────────────────
  const coStatus = (otherDone) => {
    if (!coName) return null;
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: otherDone ? '#166534' : '#9ca3af' }}>
        {otherDone ? '✅' : '⏳'} {coName} {otherDone ? 'has completed this step' : '— waiting on them'}
      </div>
    );
  };

  // ── Checklist data ────────────────────────────────────────────────────────
  const items = [];
  // 1. Sign CD — both participants
  items.push({
    id: 'cd',
    done: myCdSigned,
    // 🔑 Waiting on the borrower only once Doorly has issued it (the one light-blue item, 2026-11-02).
    actionable: !!d.cd_available,
    label: 'Sign Closing Disclosure',
    description: 'Review and electronically sign your CD. Federal rules require a 3-day review period between signing and closing.',
    content: (
      <div style={{ marginTop: 12 }}>
        {!d.cd_available && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#6b7280' }}>
            Awaiting Closing Disclosure from Doorly. We'll notify you as soon as it's ready.
          </div>
        )}
        {d.cd_available && !myCdSigned && (
          <button onClick={handleSignCd} disabled={acting}
            style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
            {acting ? 'Opening signing session…' : 'Review & Sign CD'}
          </button>
        )}
        {myCdSigned && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600 }}>
            ✓ CD Signed{myCdSignedAt ? ` on ${shortDate(myCdSignedAt)}` : ''}
          </div>
        )}
        {coStatus(otherCdSigned)}
      </div>
    ),
  });

  // 2. Upload HOI — primary borrower only
  if (!d.is_coborrower) {
    items.push({
      id: 'hoi',
      done: hoiDone,
      label: 'Upload HOI Declaration Page',
      description: 'Send us your homeowner’s insurance declaration page so we can finalize escrow figures before closing.',
      content: (
        <div style={{ marginTop: 12 }}>
          {!hoiDone && (
            <div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <a
                  href="https://quotes.matic.com/home/31b0cc25-56-44b6b62ca4c5706acc53e3/questions?utm_source=main_nav&utm_medium=website&utm_campaign=home_cta"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    // Conversion tracker — same endpoint the modal version
                    // posts to so analytics counts both entry points.
                    fetch(`${API_URL}/api/closing/hoi-shop-click`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ provider: 'matic' }),
                      keepalive: true,
                    }).catch(() => {});
                  }}
                  style={{ display: 'inline-block', padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                  Get Insurance →
                </a>
                {/* Opens the mortgagee clause + coverage requirements in a
                    popup so the borrower can hand them to their agent,
                    rather than crowding the dashboard step. */}
                <button
                  onClick={() => setShowHoiInstr(true)}
                  type="button"
                  style={{ padding: '10px 18px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  View Instructions
                </button>
              </div>
              {/* Upload path preserved as a secondary link — borrowers
                  who already have coverage skip the Matic shopping flow. */}
              <button
                onClick={() => setShowHoiModal(true)}
                disabled={acting}
                style={{ display: 'block', marginTop: 8, background: 'none', border: 'none', padding: 0, color: '#6b7280', fontSize: 12, fontWeight: 600, textDecoration: 'underline', cursor: acting ? 'not-allowed' : 'pointer' }}>
                Already have a policy? Upload your declarations page →
              </button>
            </div>
          )}
          {hoiDone && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>✓ HOI Uploaded{d.hoi_uploaded_at ? ` on ${shortDate(d.hoi_uploaded_at)}` : ''}</span>
              <button onClick={() => setShowHoiModal(true)} type="button"
                style={{ background: 'none', border: 'none', color: '#3a6ad9', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Replace
              </button>
            </div>
          )}
          {/* Hidden file input — shared by the inline Replace button
              above AND the modal's Upload Dec Page button. */}
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" onChange={handleHoiUpload} style={{ display: 'none' }} />
        </div>
      ),
    });
  }

  // 3. Set Up Your Utilities — both participants, OPTIONAL. Surfaced
  // ahead of Send Cash to Close so the borrower can knock it out
  // alongside the other pre-closing prep, but flagged optional in the
  // renderer so it doesn't gate progress. Click opens UtilityConnectModal
  // (same modal the standalone UtilitySetupCard uses). `done` flips when the
  // UtilityConnect widget posts a submission message, which the listener above
  // persists via /api/closing/utilities-complete → d.utilities_completed.
  items.push({
    id: 'utilities',
    done: !!d.utilities_completed,
    optional: true,
    label: 'Set Up Your Utilities',
    description: 'Connect electricity, gas, water, and internet before your move-in date — all in one place.',
    content: (
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={() => setUtilityModalOpen(true)}
          style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          ⚡ Set Up Utilities
        </button>
      </div>
    ),
  });

  // 4. Send Cash to Close — primary borrower only. The step is ALWAYS
  //    listed so the borrower can see it coming, but the live amount and
  //    wire instructions only populate AFTER the CD is signed. Surfacing
  //    the wire details before the CD is signed would let the borrower
  //    wire against a figure that may not match the final CD; until then
  //    we render a "coming soon" placeholder so the step isn't a surprise
  //    after signing. For solo files: primary signed. For joint: both
  //    signatures. Co-borrower is excluded entirely (the primary handles
  //    the wire). The Cash to Close fields (cd_cash_to_close,
  //    net_cash_to_close) are computed from the CD.
  const bothCdSigned = myCdSigned && (d.is_coborrower
    ? true /* coborrower path is excluded entirely; never reaches here */
    : (d.has_coborrower ? otherCdSigned : true));
  if (!d.is_coborrower) {
    items.push({
      id: 'ctc',
      done: bothCdSigned && (ctcVerified || ctcSent),
      actionable: bothCdSigned,
      label: 'Send Cash to Close',
      description: bothCdSigned
        ? 'Wire your remaining cash to close to the title company. Wire must be received no later than 3 business days before closing.'
        : 'Your exact amount and wire instructions will be provided here once your Closing Disclosure is generated and signed.',
      content: bothCdSigned ? (
        <div style={{ marginTop: 12 }}>
          {!ctcSent && (
            <>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#000', marginBottom: 6 }}>⚠️ Wire Fraud Warning</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  <strong>Never trust wire instructions sent via email.</strong> Always verify instructions directly within this platform.
                </div>
              </div>
              {/* Cash to Close breakdown — net amount = CD total minus
                  the verified down payment. When admin hasn't yet verified
                  the down payment, show the full CD amount and surface a
                  note that the credit applies once confirmed. When no CD
                  exists yet, show the placeholder copy. */}
              {d.cd_cash_to_close == null && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
                  Your final cash to close amount will be available once your Closing Disclosure is generated.
                </div>
              )}
              {d.cd_cash_to_close != null && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 12, fontSize: 13, color: '#374151' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Cash to Close Breakdown</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>Total Cash to Close</span>
                    <span style={{ fontFamily: 'monospace' }}>{fmt$(d.cd_cash_to_close)}</span>
                  </div>
                  {d.down_payment_verified && Number(d.down_payment_amount_sent || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: '#2d6a0a' }}>
                      <span>Down Payment Already Sent</span>
                      <span style={{ fontFamily: 'monospace' }}>−{fmt$(d.down_payment_amount_sent)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', marginTop: 8, paddingTop: 8, fontWeight: 700, color: '#111827' }}>
                    <span>Remaining to Wire</span>
                    <span style={{ fontFamily: 'monospace' }}>{fmt$(d.net_cash_to_close != null ? d.net_cash_to_close : d.cd_cash_to_close)}</span>
                  </div>
                  {!d.down_payment_verified && Number(d.down_payment_amount_sent || 0) > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#374151', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                      Your down payment credit will be applied once confirmed by Doorly.
                    </div>
                  )}
                </div>
              )}
              {/* Legacy fallback when neither cd_cash_to_close nor net_cash_to_close
                  are populated but cash_to_close_amount is — preserves the prior
                  display so we don't regress for existing rows. */}
              {d.cd_cash_to_close == null && d.cash_to_close_amount && (
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                  Amount due: <strong>{fmt$(d.cash_to_close_amount)}</strong>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <button onClick={() => setShowWireCTC(true)}
                  style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                  View Wire Instructions
                </button>
                <button onClick={handleCtcSent} disabled={acting}
                  style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: acting ? 'default' : 'pointer', fontWeight: 700, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                  {acting ? 'Confirming…' : '✓ I’ve Sent the Wire'}
                </button>
              </div>
            </>
          )}
          {ctcSent && !ctcVerified && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
              ✓ Wire notification received — Doorly is confirming receipt with title.
            </div>
          )}
          {ctcVerified && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600 }}>
              ✓ Cash to Close received and verified.
            </div>
          )}
        </div>
      ) : (
        // Pre-CD-signing placeholder. The step is visible so the borrower
        // knows the wire is coming, but the amount/instructions are
        // withheld until the CD is generated, reviewed, and signed.
        <div style={{ marginTop: 12 }}>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
            Your exact cash-to-close amount and wire instructions will appear here once your <strong>Closing Disclosure</strong> has been generated and reviewed. You’ll complete this step right after you sign your Closing Disclosure — there’s nothing to do yet.
          </div>
        </div>
      ),
    });
  }

  // E-Closing intentionally NOT pushed here. The dedicated E-Closing
  // surface lives on HeroCtc (clear_to_close stage hero) so the
  // checklist task should only appear after the borrower advances out
  // of closing → clear_to_close, not while they're still in closing.
  // CD-signing + cash-to-close need to complete first.

  // 🔑 THE HEADER IS THE PAGE'S MAIN CARD NOW (the Home Shopping page redesign, 2026-11-02): "Closing · [address] ·
  // $[price]" and "Your closing is on [Weekday, Mon D]." (components/dashboard/JourneyMainCard) replace the blue "You’re
  // Almost Home" card and the green "Closing scheduled" banner. The checklist is a white card, the one item waiting on
  // the borrower in light blue (constants/journeySteps CHECK_COLOURS).
  const waitingAt = isCoBorrower ? -1 : waitingIndex(items);
  return (
    <div className="mb-4" data-testid="closing-checklist-hero">
      {isCoBorrower && primaryName && (
        <p style={{ fontSize: 13.5, color: '#000', lineHeight: 1.6, margin: '0 0 12px' }}>{`${primaryName} is handling the wire and HOI upload.`}</p>
      )}

      {errorMsg && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#000', marginBottom: 12 }}>{errorMsg}</div>
      )}

      {/* Checklist */}
      <div data-testid="dashboard-checklist" style={{ ...CHECK_COLOURS.card, borderRadius: 16, padding: 22 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 16 }}>Your Checklist</div>
        {items.map((item, i) => (
          <div key={item.id} data-testid="checklist-item" data-waiting={i === waitingAt ? 'true' : 'false'} data-done={item.done ? 'true' : 'false'}
            style={i === waitingAt
              ? { ...CHECK_COLOURS.waiting, borderRadius: 10, padding: '10px 12px', margin: '8px 0' }
              : { paddingTop: i === 0 ? 0 : 16, paddingBottom: 16, borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: item.done ? CHECK_COLOURS.doneTick : '#f3f4f6', color: item.done ? '#fff' : '#6b7280', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                {item.done ? '✓' : (i + 1)}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: item.done ? '#9ca3af' : '#111827', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
              {item.optional && !item.done && (
                <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '2px 8px', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Optional
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 0 34px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{item.description}</p>
            <div style={{ marginLeft: 34 }}>{item.content}</div>
          </div>
        ))}
      </div>

      {/* Wire instructions modal — reuses the EMD modal with wireType='ctc' */}
      <WireInstructionsModal
        isOpen={showWireCTC}
        onClose={() => setShowWireCTC(false)}
        wireType="ctc"
        loanNumber={d.loan_number}
        borrowerName={name}
        amount={d.net_cash_to_close != null ? d.net_cash_to_close : d.cash_to_close_amount}
        wireData={d.wire_instructions}
      />

      {utilityModalOpen && <UtilityConnectModal onClose={() => setUtilityModalOpen(false)} />}

      {/* HOI Modal — two options: shop a partner-pre-filled quote
          (Matic deep-link placeholder until partner account is wired)
          OR upload the existing dec page. The Upload button forwards
          to the existing hidden fileInputRef + handleHoiUpload pair
          declared in the HOI checklist item above so we don't
          duplicate the upload handler. Backdrop click is allowed —
          this is a single-decision modal (pick shop or pick upload)
          and accidental dismissal is recoverable. */}
      {showHoiInstr && (
        <div onClick={() => setShowHoiInstr(false)} className="safe-modal-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: '#fff',
            borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}>
            <div style={{
              background: '#3a6ad9',
              padding: '18px 22px', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#fffd5b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  For Your Insurance Agent
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  HOI Policy Instructions
                </div>
              </div>
              <button onClick={() => setShowHoiInstr(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                ✕ Close
              </button>
            </div>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#3a6ad9', marginBottom: 8 }}>
                Give your insurance agent these details
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
                <strong>Mortgagee / loss-payee clause</strong> — your policy must list exactly:
              </div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: '#111827', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 12 }}>
                {`Go Doorly, LLC ISAOA/ATIMA\n8708 S Congress Ave, Ste E500-202\nAustin, TX 78745\nLoan #: ${d.loan_number || '—'}`}
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                <strong>Coverage requirements:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  <li>Dwelling (Coverage A) at least the <strong>greater of your loan amount or 100% replacement cost</strong></li>
                  <li>Policy <strong>effective on or before your closing date</strong> (and not expired)</li>
                  <li>First-year (12-month) premium <strong>paid at or before closing</strong></li>
                  <li>Property address must match the home you’re purchasing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHoiModal && (
        <div onClick={() => setShowHoiModal(false)} className="safe-modal-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: '#fff',
            borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #111827 0%, #1e3a5f 100%)',
              padding: '18px 22px', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#acf44e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  Required Before Closing
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  Homeowner's Insurance
                </div>
              </div>
              <button onClick={() => setShowHoiModal(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                ✕ Close
              </button>
            </div>

            <div style={{ padding: '22px 24px' }}>
              {/* Option 1 — Shop (Matic placeholder) */}
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
                  Option 1 — Shop for Coverage
                </div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 14 }}>
                  Get quotes from top-rated carriers in minutes.
                  Your property details are pre-filled — just answer a few
                  questions to compare rates and bind coverage instantly.
                </div>

                {/* Pre-fill summary */}
                {(d.borrower_purchase_price || d.property_address) && (
                  <div style={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
                    {d.property_address && (
                      <div>📍 {d.property_address}</div>
                    )}
                    {d.borrower_purchase_price && (
                      <div>💰 Purchase price: ${Number(d.borrower_purchase_price).toLocaleString()}</div>
                    )}
                    {d.closing_date && (
                      <div>📅 Closing: {new Date(d.closing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    )}
                  </div>
                )}

                <a
                  href={(() => {
                    // Matic deep-link with pre-fill params. URL is a
                    // placeholder until the partner account + sign-on
                    // URL pattern is finalized. Replace base + params
                    // shape per Matic's onboarding doc when ready.
                    const base = 'https://matic.com/get-a-quote';
                    const params = new URLSearchParams();
                    if (d.property_address) params.set('address', d.property_address);
                    if (d.borrower_purchase_price) params.set('purchase_price', d.borrower_purchase_price);
                    if (d.closing_date) params.set('closing_date', String(d.closing_date).slice(0, 10));
                    params.set('source', 'doorly');
                    params.set('loan_type', 'owner_finance');
                    return `${base}?${params.toString()}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    fetch(`${API_URL}/api/closing/hoi-shop-click`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ provider: 'matic' }),
                      keepalive: true,
                    }).catch(() => {});
                  }}
                  style={{ display: 'inline-block', background: '#3a6ad9', color: '#fff', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  Shop for Insurance →
                </a>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>
                  Powered by Matic Insurance
                </div>
              </div>

              {/* Option 2 — Upload existing dec page */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                  Option 2 — I Already Have Coverage
                </div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 12 }}>
                  Upload your declarations page (dec page). Your policy must
                  name the lender as an additional insured and meet minimum
                  coverage requirements.
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={acting}
                  style={{ background: '#fff', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: acting ? 'not-allowed' : 'pointer' }}>
                  {acting ? 'Uploading…' : '📎 Upload Dec Page'}
                </button>
                {errorMsg && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#991b1b' }}>{errorMsg}</div>
                )}
              </div>

              {/* RESPA disclaimer */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
                <strong style={{ color: '#374151' }}>Important Notice:</strong>{' '}
                Go Doorly, LLC is not an insurance agent, broker, or carrier.
                You are not required to obtain insurance through any
                particular provider or agent. You have the right to shop
                for homeowner's insurance with any licensed provider of
                your choice. Any insurance referral is provided as a
                convenience only and does not constitute an endorsement.
                Doorly does not receive compensation for insurance
                referrals. Coverage must meet your lender's minimum
                requirements as specified in your loan documents.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HeroNewConstructionClosing — combined contract + closing hero ──────────
// New construction (TX builder contracts) has NO option period, so these
// borrowers route straight from offer_accepted → closing (see finalizeAbPsa /
// boldsign). They still owe the "contract tasks" that a resale borrower would
// have completed during the option period — down payment wire, PSA signing,
// appraisal — but with NO inspection (builders disallow third-party
// inspections during construction) and NO option-period language (no
// countdown, no termination decision).
//
// This hero fetches BOTH endpoints — /api/option-period/dashboard (contract
// tasks) and /api/closing/status (closing tasks) — and renders them as two
// sections. The option-period action endpoints are userId-gated (no stage
// guard), so they work normally while the borrower sits in 'closing'. Reuses
// the shared PSASigningModal / WireInstructionsModal / UtilityConnectModal so
// the existing HeroOptionPeriod + HeroClosing components stay untouched.
function HeroNewConstructionClosing({ name, token, isCoBorrower, primaryName, underContract = false }) {
  const navigate = useNavigate();
  const [opData, setOpData]   = useState(null);   // /api/option-period/dashboard
  const [clData, setClData]   = useState(null);   // /api/closing/status
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showWireDP, setShowWireDP]   = useState(false);
  const [showWireCTC, setShowWireCTC] = useState(false);
  const [psaModalOpen, setPsaModalOpen] = useState(false);
  const [utilityModalOpen, setUtilityModalOpen] = useState(false);
  const [showHoiInstr, setShowHoiInstr] = useState(false);
  const [appraisalReportOpen, setAppraisalReportOpen] = useState(false);
  // Congrats popup when the borrower completes the 4th NC task and the
  // backend auto-flips the loan to 'closing'. Drives a centered modal
  // (AdvancedToClosingModal at the bottom of this component's render).
  const [showAdvancedToClosing, setShowAdvancedToClosing] = useState(false);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    if (!token) return Promise.resolve();
    return Promise.allSettled([
      axios.get(`${API_URL}/api/option-period/dashboard`, { headers }),
      axios.get(`${API_URL}/api/closing/status`, { headers }),
    ]).then(([op, cl]) => {
      if (op.status === 'fulfilled') setOpData(op.value.data);
      if (cl.status === 'fulfilled') setClData(cl.value.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark the utilities step complete when the UtilityConnect widget posts a
  // submission message (same cross-origin listener as HeroClosing).
  const utilDoneRef = useRef(false);
  useEffect(() => {
    const markUtilitiesComplete = async () => {
      if (utilDoneRef.current) return;
      utilDoneRef.current = true;
      try {
        await axios.post(`${API_URL}/api/closing/utilities-complete`, {}, { headers });
        await load();
      } catch (_) { utilDoneRef.current = false; }
    };
    const SUBMIT_RE = /submit|complete|success|thank|confirmation|\blead\b/i;
    const onMsg = (e) => {
      if (e.origin !== 'https://www.utilityconnect.net') return;
      const data = e.data;
      if (typeof data === 'string') {
        if (data.startsWith('[iFrameSizer]')) return;
        if (SUBMIT_RE.test(data)) markUtilitiesComplete();
        return;
      }
      if (data && typeof data === 'object') {
        const fields = [data.type, data.event, data.action, data.status, data.name, data.message]
          .filter(Boolean).map(String).join(' ');
        if (SUBMIT_RE.test(fields) || SUBMIT_RE.test(JSON.stringify(data))) markUtilitiesComplete();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Down-payment / contract actions hit the option-period endpoints (userId
  // gated — fine while in closing). Refresh both payloads afterward.
  // review-complete returns advancedToClosing=true when this click was the
  // 4th task and the backend just flipped users.application_status to
  // 'closing' — surface that via the congrats modal.
  const act = async (path, body = {}) => {
    setActing(true); setErrorMsg('');
    try {
      const resp = await axios.post(`${API_URL}/api/option-period/${path}`, body, { headers });
      if (resp?.data?.advancedToClosing) setShowAdvancedToClosing(true);
      load();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-8 space-y-4 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-2xl" />
        <div className="h-48 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  const op = opData?.option_period || {};
  const d  = opData || {};
  const c  = clData || {};
  const firstName = name ? name.split(' ')[0] : '';

  const fmt$ = (v) => (v == null || v === '' ? null : '$' + Number(v).toLocaleString());
  const shortDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const longDate = (v) => {
    if (!v) return null;
    const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
    const [y, m, day] = s.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // ── Contract tasks (from the option-period dashboard payload) ──────────────
  const emdDone = !!d.tasks?.emd_wired;
  const contractItems = [];

  // 1. Send Down Payment — primary only (co-borrower waits on primary's wire).
  if (!isCoBorrower) {
    contractItems.push({
      id: 'nc-down-payment',
      done: !!d.down_payment_verified || !!d.down_payment_sent || emdDone,
      label: 'Send Down Payment',
      description: 'Secure your contract by sending your down payment via wire. The Purchase Initiation Deposit you already paid will be credited on your Closing Disclosure.',
      content: (
        <div>
          {d.down_payment_credit_available && Number(d.down_payment_credit_amount || 0) > 0 && !d.down_payment_sent && !emdDone && (
            <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#166534' }}>
              💰 <strong>Credit Available:</strong> ${Number(d.down_payment_credit_amount).toLocaleString()} from your previous transaction will be applied to your down payment.
            </div>
          )}
          {!d.down_payment_sent && !emdDone && (
            <div style={{ marginTop: 12 }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#000', marginBottom: 6 }}>⚠️ Wire Fraud Warning</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  <strong>Never trust wire instructions sent via email.</strong> Always verify instructions directly within this platform. Your down payment must be sent via wire transfer — Doorly does not support ACH.
                </div>
              </div>
              {/* Same closed state as the option-period checklist above —
                  disabled, not hidden. See the note there. */}
              {d.blocked_reason ? (
                <div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <button disabled aria-disabled="true"
                      style={{ padding: '10px 18px', background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 10, cursor: 'not-allowed', fontWeight: 700, fontSize: 13 }}>
                      View Wire Instructions
                    </button>
                    <button disabled aria-disabled="true"
                      style={{ padding: '10px 18px', background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 10, cursor: 'not-allowed', fontWeight: 800, fontSize: 13 }}>
                      ✓ I Have Sent the Wire
                    </button>
                  </div>
                  <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600, lineHeight: 1.6 }}>
                    ⏳ {d.blocked_reason} Nothing is owed yet and nothing has been missed.
                  </div>
                </div>
              ) : d.down_payment_credit_available && Number(d.down_payment_credit_amount || 0) >= Number(d.emd_remaining || 0) && Number(d.emd_remaining || 0) > 0 ? (
                <button onClick={() => act('apply-credit')} disabled={acting}
                  style={{ width: '100%', padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                  {acting ? 'Confirming…' : '✓ Confirm Credit Application — No Wire Required'}
                </button>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <button onClick={() => setShowWireDP(true)}
                    style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                    View Wire Instructions
                  </button>
                  <button onClick={() => act('emd')} disabled={acting}
                    style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                    {acting ? 'Confirming…' : '✓ I Have Sent the Wire'}
                  </button>
                </div>
              )}
            </div>
          )}
          {(d.down_payment_sent || emdDone) && !d.down_payment_verified && (
            <div style={{ marginTop: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
              ⏳ Sent — awaiting confirmation from Doorly
            </div>
          )}
          {d.down_payment_verified && (
            <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 700 }}>
              ✓ Confirmed received by Doorly{d.down_payment_amount_sent ? ` — $${Number(d.down_payment_amount_sent).toLocaleString()}` : ''}
            </div>
          )}
        </div>
      ),
    });
  }

  // 2. Sign Purchase Contract (PSA) — identical state machine to the
  // Option Period PSA item above. Both checklists drive off the same
  // option_periods columns (psa_package_status + psa_signed_*_at), so
  // there's no good reason for them to render different copy. Earlier
  // versions diverged and the Under Contract path missed the
  // "neither-signed-but-package-not-yet-stamped" case → borrowers got
  // stuck on "being prepared" even after admin Step 7 generated the PSA.
  contractItems.push({
    id: 'nc-psa',
    done: op.psa_package_status === 'completed',
    actionable: !!op.psa_package_status,
    label: 'Sign Purchase Contract (PSA)',
    description: 'Your Purchase and Sale Agreement with Doorly. Sign to confirm the terms of your home purchase.',
    content: (() => {
      const pkgStatus = op.psa_package_status || null;
      const primarySigned = !!op.psa_signed_primary_at;
      const coSigned      = !!op.psa_signed_coborrower_at;
      const fullySigned   = pkgStatus === 'completed';
      const declined      = pkgStatus === 'declined';
      const expired       = pkgStatus === 'expired';
      const revoked       = pkgStatus === 'revoked';
      const hasCoBorrower = !!isCoBorrower || !!op.psa_signed_coborrower_at;

      if (fullySigned) {
        return (
          <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 700 }}>
            ✓ Your Purchase Contract has been fully executed and can be viewed on your Documents page under "Property Documents".
          </div>
        );
      }
      if (declined || expired || revoked) {
        const label = declined ? 'declined' : expired ? 'expired' : 'revoked';
        return (
          <div style={{ marginTop: 8, color: '#374151', fontWeight: 600, fontSize: 13 }}>
            ⚠ Purchase Contract signing was {label}. Doorly is reaching out to re-send. Contact 512-713-6016 if urgent.
          </div>
        );
      }
      if (primarySigned && (!hasCoBorrower || coSigned) &&
          (pkgStatus === 'awaiting_doorly_countersign' || pkgStatus === 'sent')) {
        return (
          <div style={{ marginTop: 8, color: '#374151', fontWeight: 600, fontSize: 13 }}>
            ✓ Your Purchase Contract has now been routed to Doorly to countersign.
          </div>
        );
      }
      if (primarySigned && hasCoBorrower && !coSigned) {
        if (isCoBorrower) {
          return (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setPsaModalOpen(true)}
                style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review & Sign →
              </button>
            </div>
          );
        }
        return (
          <div style={{ marginTop: 8, color: '#2d6a0a', fontWeight: 600, fontSize: 13 }}>
            ✓ Your signature is complete. Waiting for co-borrower to sign.
          </div>
        );
      }
      if (!primarySigned && hasCoBorrower && coSigned) {
        if (isCoBorrower) {
          return (
            <div style={{ marginTop: 8, color: '#2d6a0a', fontWeight: 600, fontSize: 13 }}>
              ✓ Your signature is complete. Waiting for primary borrower to sign.
            </div>
          );
        }
        return (
          <div style={{ marginTop: 8 }}>
            <button onClick={() => setPsaModalOpen(true)}
              style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Review & Sign →
            </button>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
              ✓ Your co-borrower has already signed — your signature completes the contract.
            </div>
          </div>
        );
      }
      if (!primarySigned && !coSigned) {
        return (
          <div style={{ marginTop: 8 }}>
            <button onClick={() => setPsaModalOpen(true)}
              style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Review & Sign →
            </button>
          </div>
        );
      }
      if (pkgStatus === 'sent' || pkgStatus === 'awaiting_doorly_countersign') {
        return (
          <div style={{ marginTop: 8 }}>
            <button onClick={() => setPsaModalOpen(true)}
              style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Review & Sign →
            </button>
          </div>
        );
      }
      return (
        <p style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginTop: 8 }}>
          Your Purchase Contract is being prepared — you'll be able to sign here once it's ready.
        </p>
      );
    })(),
  });

  // 3. Appraisal — read-only status. NO inspection (builders disallow
  //    third-party inspections during construction), so this is appraisal
  //    only with no order CTA, no inspection notice, no seller-credit flow.
  contractItems.push((() => {
    const apprStatus = (op.appraisal_status || '').toLowerCase();
    let badge, badgeBg, badgeColor, message, doneState;
    if (!op.inspection_appraisal_ordered) {
      badge = 'Pending'; badgeBg = '#f3f4f6'; badgeColor = '#6b7280';
      message = 'Doorly to order. Pending receipt of down payment wire and executed contract.';
      doneState = false;
    } else if (apprStatus === 'complete' || op.appraisal_report_text) {
      badge = 'Complete'; badgeBg = '#f0fdf4'; badgeColor = '#166534';
      message = 'Your appraisal is complete.';
      doneState = true;
    } else if (apprStatus === 'scheduled' || op.appraisal_scheduled_at) {
      badge = 'Scheduled'; badgeBg = '#f3f4f6'; badgeColor = '#374151';
      const when = op.appraisal_scheduled_at ? shortDate(op.appraisal_scheduled_at) : null;
      message = when ? `Appraisal scheduled for ${when}.` : 'Your appraisal has been scheduled.';
      doneState = false;
    } else {
      badge = 'Order Placed'; badgeBg = '#eff6ff'; badgeColor = '#1d4ed8';
      message = "Order placed. We're scheduling your appraisal — you'll be notified when your report is ready.";
      doneState = false;
    }
    return {
      id: 'nc-appraisal',
      done: doneState,
      actionable: false,   // Doorly orders it
      label: 'Appraisal',
      description: message,
      content: (
        <div style={{ marginTop: 12 }}>
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 9999, background: badgeBg, color: badgeColor, fontSize: 12, fontWeight: 700 }}>
            {badge}
          </span>
        </div>
      ),
    };
  })());

  // 3.5 Independent inspection notice — mirrors the Option Period
  // hero's notice card (Dashboard.js:2005). Reasonable for finished NC
  // homes (final walkthrough / pre-close), even though the appraisal
  // card above is appraisal-only. Closing date is used as the
  // deadline reference since NC has no option period.
  contractItems.push({
    id: 'nc-independent-inspection-notice',
    kind: 'notice',
    content: (
      <div style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: '16px 20px',
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#000', marginBottom: 6 }}>
          🔍 Independent Home Inspection
        </div>
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          You are welcome to obtain your own independent home inspection
          at your expense. It must be completed before closing
          {c.closing_date
            ? ` (${longDate(c.closing_date)})`
            : ''}.
        </p>
      </div>
    ),
  });

  // 4. Review Appraisal Reports — mirrors the option-period review step, but
  //    appraisal-only (no inspection / seller-credit flow for new construction).
  //    Borrower reviews the report, then confirms via review-complete; done
  //    tracks report_review_status from the option-period payload.
  contractItems.push({
    id: 'nc-review-reports',
    done: op.report_review_status === 'complete',
    actionable: !!(op.appraisal_status === 'complete' || op.appraisal_report_text),
    label: 'Review Appraisal Report',
    description: op.report_review_status === 'complete'
      ? "You've reviewed your appraisal report."
      : (op.appraisal_status === 'complete' || op.appraisal_report_text)
        ? 'Your appraisal report is ready. Open it to review, then confirm.'
        : 'Your appraisal report will be available here once your appraisal is complete.',
    content: (op.appraisal_status === 'complete' || op.appraisal_report_text) ? (
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button onClick={() => setAppraisalReportOpen(true)}
            style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            View Report →
          </button>
          {op.report_review_status !== 'complete' && (
            <button onClick={() => act('review-complete')} disabled={acting}
              style={{ padding: '10px 18px', background: 'transparent', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 10, cursor: acting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}>
              {acting ? 'Saving…' : '✓ Mark as Reviewed'}
            </button>
          )}
          {op.report_review_status === 'complete' && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px',
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9999,
              fontSize: 12, fontWeight: 700, color: '#166534',
            }}>
              <span aria-hidden="true">✓</span>
              {op.report_review_completed_at
                ? `Reviewed on ${new Date(op.report_review_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : 'Reviewed'}
            </span>
          )}
        </div>
      </div>
    ) : (
      <div style={{ marginTop: 12 }}>
        <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 9999, background: '#f3f4f6', color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Pending</span>
      </div>
    ),
  });

  // ── Closing tasks (from the closing-status payload) ────────────────────────
  const myCdSigned   = c.is_coborrower ? !!c.cd_coborrower_signed : !!c.cd_signed;
  const myCdSignedAt = c.is_coborrower ? c.cd_coborrower_signed_at : c.cd_signed_at;
  const otherCdSigned = c.is_coborrower ? !!c.cd_signed : !!c.cd_coborrower_signed;
  const bothCdSigned  = myCdSigned && (c.is_coborrower ? true : (c.has_coborrower ? otherCdSigned : true));
  const hoiDone      = !!c.hoi_uploaded;
  const ctcSent      = !!c.ctc_wire_sent;
  const ctcVerified  = !!c.ctc_wire_verified;

  const handleHoiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setActing(true); setErrorMsg('');
    const fd = new FormData();
    fd.append('files', file);
    fd.append('document_type', 'hoi_declaration');
    try {
      await axios.post(`${API_URL}/api/documents/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setActing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCtcSent = async () => {
    setActing(true); setErrorMsg('');
    try {
      const amountWired = c.net_cash_to_close ?? c.cd_cash_to_close ?? c.cash_to_close_amount ?? null;
      await axios.post(`${API_URL}/api/closing/wire-sent`, { amount: amountWired }, { headers });
      load();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Could not mark wire as sent.');
    } finally {
      setActing(false);
    }
  };

  const closingItems = [];
  closingItems.push({
    id: 'nc-cd',
    done: myCdSigned,
    actionable: !!c.cd_available,
    label: 'Sign Closing Disclosure',
    description: 'Review and electronically sign your CD. Federal rules require a 3-day review period between signing and closing.',
    content: (
      <div style={{ marginTop: 12 }}>
        {!c.cd_available && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#6b7280' }}>
            Awaiting Closing Disclosure from Doorly. We'll notify you as soon as it's ready.
          </div>
        )}
        {c.cd_available && !myCdSigned && (
          <button onClick={() => navigate('/portal/closing-disclosures')} disabled={acting}
            style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
            Review & Sign CD
          </button>
        )}
        {myCdSigned && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600 }}>
            ✓ CD Signed{myCdSignedAt ? ` on ${shortDate(myCdSignedAt)}` : ''}
          </div>
        )}
      </div>
    ),
  });

  if (!c.is_coborrower) {
    closingItems.push({
      id: 'nc-hoi',
      done: hoiDone,
      label: 'Upload HOI Declaration Page',
      description: 'Send us your homeowner’s insurance declaration page so we can finalize escrow figures before closing.',
      content: (
        <div style={{ marginTop: 12 }}>
          {!hoiDone && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => fileInputRef.current?.click()} disabled={acting}
                style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                {acting ? 'Uploading…' : '📎 Upload Dec Page'}
              </button>
              <button onClick={() => setShowHoiInstr(true)} type="button"
                style={{ padding: '10px 18px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                View Instructions
              </button>
            </div>
          )}
          {hoiDone && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>✓ HOI Uploaded{c.hoi_uploaded_at ? ` on ${shortDate(c.hoi_uploaded_at)}` : ''}</span>
              <button onClick={() => fileInputRef.current?.click()} type="button"
                style={{ background: 'none', border: 'none', color: '#3a6ad9', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Replace
              </button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" onChange={handleHoiUpload} style={{ display: 'none' }} />
        </div>
      ),
    });
  }

  closingItems.push({
    id: 'nc-utilities',
    done: !!c.utilities_completed,
    optional: true,
    label: 'Set Up Your Utilities',
    description: 'Connect electricity, gas, water, and internet before your move-in date — all in one place.',
    content: (
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => setUtilityModalOpen(true)}
          style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          ⚡ Set Up Utilities
        </button>
      </div>
    ),
  });

  if (!c.is_coborrower) {
    closingItems.push({
      id: 'nc-ctc',
      done: bothCdSigned && (ctcVerified || ctcSent),
      actionable: bothCdSigned,
      label: 'Send Cash to Close',
      description: bothCdSigned
        ? 'Wire your remaining cash to close to the title company. Wire must be received no later than 3 business days before closing.'
        : 'Your exact amount and wire instructions will be provided here once your Closing Disclosure is generated and signed.',
      content: bothCdSigned ? (
        <div style={{ marginTop: 12 }}>
          {!ctcSent && (
            <>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#000', marginBottom: 6 }}>⚠️ Wire Fraud Warning</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  <strong>Never trust wire instructions sent via email.</strong> Always verify instructions directly within this platform.
                </div>
              </div>
              {c.cd_cash_to_close != null && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 12, fontSize: 13, color: '#374151' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: 'none', fontWeight: 700, color: '#111827' }}>
                    <span>Remaining to Wire</span>
                    <span style={{ fontFamily: 'monospace' }}>{fmt$(c.net_cash_to_close != null ? c.net_cash_to_close : c.cd_cash_to_close)}</span>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <button onClick={() => setShowWireCTC(true)}
                  style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                  View Wire Instructions
                </button>
                <button onClick={handleCtcSent} disabled={acting}
                  style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, cursor: acting ? 'default' : 'pointer', fontWeight: 700, fontSize: 13, opacity: acting ? 0.5 : 1 }}>
                  {acting ? 'Confirming…' : '✓ I’ve Sent the Wire'}
                </button>
              </div>
            </>
          )}
          {ctcSent && !ctcVerified && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
              ✓ Wire notification received — Doorly is confirming receipt with title.
            </div>
          )}
          {ctcVerified && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534', fontWeight: 600 }}>
              ✓ Cash to Close received and verified.
            </div>
          )}
        </div>
      ) : (
        // Pre-CD-signing placeholder — the step is visible so the borrower
        // knows the wire is coming, but the amount/instructions are withheld
        // until the CD is generated, reviewed, and signed.
        <div style={{ marginTop: 12 }}>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
            Your exact cash-to-close amount and wire instructions will appear here once your <strong>Closing Disclosure</strong> has been generated and reviewed. You’ll complete this step right after you sign your Closing Disclosure — there’s nothing to do yet.
          </div>
        </div>
      ),
    });
  }

  // ── Shared checklist renderer ──────────────────────────────────────────────
  const renderChecklist = (sectionTitle, items) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 22, marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 16 }}>{sectionTitle}</div>
      {(() => {
        // Pre-compute the visible step number for each non-notice row so
        // notices don't punch a hole in the 1-2-3 sequence.
        let stepNum = 0;
        const numbers = items.map((it) => (it.kind === 'notice' ? null : ++stepNum));
        return items.map((item, i) => {
        const rowStyle = { paddingTop: i === 0 ? 0 : 16, paddingBottom: 16, borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none' };
        // kind:'notice' renders full-width content with no numbered
        // circle or label/description scaffold — used for the
        // Independent Home Inspection tip. Mirrors the same pattern
        // the Option Period checklist uses (Dashboard.js:2109).
        if (item.kind === 'notice') {
          return (
            <div key={item.id || i} id={item.id} style={rowStyle}>
              {item.content}
            </div>
          );
        }
        const waiting = !isCoBorrower && i === waitingIndex(items);
        return (
          <div key={item.id} data-testid="checklist-item" data-waiting={waiting ? 'true' : 'false'} data-done={item.done ? 'true' : 'false'}
            style={waiting ? { ...CHECK_COLOURS.waiting, borderRadius: 10, padding: '10px 12px', margin: '8px 0' } : rowStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: item.done ? CHECK_COLOURS.doneTick : '#f3f4f6', color: item.done ? '#fff' : '#6b7280', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                {item.done ? '✓' : numbers[i]}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: item.done ? '#9ca3af' : '#111827', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
              {item.optional && !item.done && (
                <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '2px 8px', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Optional
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 0 34px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{item.description}</p>
            <div style={{ marginLeft: 34 }}>{item.content}</div>
          </div>
        );
        });
      })()}
    </div>
  );

  // 🔑 The header is the page's main card (components/dashboard/JourneyMainCard, the redesign 2026-11-02).
  return (
    <div className="mb-4" data-testid="nc-checklist-hero">
      {isCoBorrower && primaryName && (
        <p style={{ fontSize: 13.5, color: '#000', lineHeight: 1.6, margin: '0 0 12px' }}>{`${primaryName} is handling the wires and HOI upload.`}</p>
      )}

      {errorMsg && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#000', marginBottom: 12 }}>{errorMsg}</div>
      )}

      {renderChecklist('Contract Tasks', contractItems)}

      {/* Down-payment wire modal (wireType="down_payment") */}
      <WireInstructionsModal
        isOpen={showWireDP}
        onClose={() => setShowWireDP(false)}
        wireType="down_payment"
        loanNumber={d.loan_number}
        borrowerName={name}
        amount={d.emd_remaining}
        wireData={d.wire_instructions}
      />

      {/* Cash-to-close wire modal (wireType="ctc") */}
      <WireInstructionsModal
        isOpen={showWireCTC}
        onClose={() => setShowWireCTC(false)}
        wireType="ctc"
        loanNumber={c.loan_number}
        borrowerName={name}
        amount={c.net_cash_to_close != null ? c.net_cash_to_close : c.cash_to_close_amount}
        wireData={c.wire_instructions}
      />

      {showHoiInstr && (
        <div onClick={() => setShowHoiInstr(false)} className="safe-modal-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: '#fff',
            borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}>
            <div style={{
              background: '#3a6ad9',
              padding: '18px 22px', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#fffd5b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  For Your Insurance Agent
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>
                  HOI Policy Instructions
                </div>
              </div>
              <button onClick={() => setShowHoiInstr(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                ✕ Close
              </button>
            </div>
            <div style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#3a6ad9', marginBottom: 8 }}>
                Give your insurance agent these details
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
                <strong>Mortgagee / loss-payee clause</strong> — your policy must list exactly:
              </div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: '#111827', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 12 }}>
                {`Go Doorly, LLC ISAOA/ATIMA\n8708 S Congress Ave, Ste E500-202\nAustin, TX 78745\nLoan #: ${c.loan_number || '—'}`}
              </div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                <strong>Coverage requirements:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  <li>Dwelling (Coverage A) at least the <strong>greater of your loan amount or 100% replacement cost</strong></li>
                  <li>Policy <strong>effective on or before your closing date</strong> (and not expired)</li>
                  <li>First-year (12-month) premium <strong>paid at or before closing</strong></li>
                  <li>Property address must match the home you’re purchasing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {utilityModalOpen && <UtilityConnectModal onClose={() => setUtilityModalOpen(false)} />}

      <PSASigningModal
        open={psaModalOpen}
        onClose={() => setPsaModalOpen(false)}
        onSigned={() => { setPsaModalOpen(false); load(); }}
        apiUrl={API_URL}
        token={token}
      />

      {/* Appraisal Report modal — opened from the 'View Report →' button on
          the Review Appraisal Report step. KPI cards at the top, full
          report rendered via iframe srcdoc so it presents as a document
          rather than another dashboard card. Mark-as-Reviewed action
          stays on the dashboard row, not in here. */}
      <AppraisalReportModal
        open={appraisalReportOpen}
        onClose={() => setAppraisalReportOpen(false)}
        appraisedValue={op.appraisal_value != null ? Number(op.appraisal_value) : null}
        contractPrice={d.contract_purchase_price != null ? Number(d.contract_purchase_price) : null}
        reportText={op.appraisal_report_text}
        reportUrl={op.appraisal_report_url}
      />

      {/* Congrats popup — fires when the borrower's last NC task flips
          users.application_status to 'closing' on the backend. Closing
          the modal navigates them straight to /dashboard so the new
          stage hero (Closing) loads after their next page render. */}
      <AdvancedToClosingModal
        open={showAdvancedToClosing}
        firstName={firstName}
        onClose={() => { setShowAdvancedToClosing(false); load(); }}
      />
    </div>
  );
}

// ─── AdvancedToClosingModal ──────────────────────────────────────────────
// Centered celebratory modal shown the moment users.application_status
// flips to 'closing' on the NC Under Contract hero. Body copy congratulates
// + sets the next-stage expectation. Single primary CTA — closing it
// triggers a load() in the parent which re-fetches /closing/status and
// transitions the hero render.
function AdvancedToClosingModal({ open, firstName, onClose }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="safe-modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 110, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460,
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <div style={{ background: '#3a6ad9', padding: '36px 24px 28px' }}>
          <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 8 }}>🎉</div>
          <h2 style={{ margin: 0, color: '#fff', fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
            You're heading to closing{firstName ? `, ${firstName}` : ''}!
          </h2>
        </div>
        <div style={{ padding: '24px 28px 8px' }}>
          <p style={{ margin: '0 0 14px', color: '#111827', fontSize: 14, lineHeight: 1.6, fontWeight: 600 }}>
            All four of your Under Contract tasks are complete.
          </p>
          <p style={{ margin: '0 0 18px', color: '#4b5563', fontSize: 13, lineHeight: 1.6 }}>
            Your loan has officially advanced to <strong style={{ color: '#111827' }}>Closing</strong>. Doorly's team is finalizing your closing package — you'll see new tasks on your dashboard as we get closer to your closing date.
          </p>
        </div>
        <div style={{ padding: '12px 28px 24px' }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: '#3a6ad9',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Continue to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AppraisalReportModal ────────────────────────────────────────────────
// Two-zone layout: three KPI cards (Appraised Value / Contract Price /
// Value vs. Contract) pinned to the top, then the appraisal report
// itself in a sandboxed iframe — srcdoc when only text is on file, src
// when a PDF/URL is available. Value-vs-Contract auto-colors positive
// (green) vs. negative (red) so the borrower's eye lands on the
// material number first.
function AppraisalReportModal({ open, onClose, appraisedValue, contractPrice, reportText, reportUrl }) {
  if (!open) return null;

  const fmt$ = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : '$' + Number(v).toLocaleString());
  const delta = (appraisedValue != null && contractPrice != null) ? (appraisedValue - contractPrice) : null;
  const deltaPct = (delta != null && contractPrice) ? (delta / contractPrice) * 100 : null;
  const deltaSign = delta == null ? '' : (delta > 0 ? '+' : delta < 0 ? '−' : '');
  const deltaColor = delta == null ? '#6b7280' : (delta > 0 ? '#166534' : delta < 0 ? '#991b1b' : '#374151');
  const deltaBg    = delta == null ? '#f3f4f6' : (delta > 0 ? '#f0fdf4' : delta < 0 ? '#fef2f2' : '#f3f4f6');

  // iframe srcdoc — render the text report as a paginated document.
  // Tailwind isn't reachable inside the iframe; inline styles only.
  const srcDoc = reportText
    ? `<!DOCTYPE html><html><head><meta charset="utf-8"/></head>
       <body style="margin:0;padding:32px 40px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px;line-height:1.65;color:#1a1a1a;background:#ffffff;white-space:pre-wrap;">${
         String(reportText).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
       }</body></html>`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="safe-modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="safe-modal-box"
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 880,
          '--sm-max': '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Appraisal Report</h3>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'transparent', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* KPI strip */}
        <div style={{ padding: '18px 24px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, borderBottom: '1px solid #f3f4f6', background: '#fafbfc' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Appraised Value</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{fmt$(appraisedValue)}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Contract Price</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{fmt$(contractPrice)}</div>
          </div>
          <div style={{ background: deltaBg, border: `1px solid ${delta == null ? '#e5e7eb' : (delta > 0 ? '#bbf7d0' : delta < 0 ? '#fecaca' : '#e5e7eb')}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Value vs. Contract</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: deltaColor, fontVariantNumeric: 'tabular-nums' }}>
              {delta == null ? '—' : `${deltaSign}${fmt$(Math.abs(delta))}`}
              {deltaPct != null && (
                <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 6, color: deltaColor, opacity: 0.85 }}>
                  ({deltaSign}{Math.abs(deltaPct).toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Report iframe */}
        <div style={{ flex: 1, minHeight: 360, padding: '12px 16px', overflow: 'hidden', background: '#f3f4f6' }}>
          {reportUrl ? (
            <iframe
              title="Appraisal Report"
              src={reportUrl}
              style={{ width: '100%', height: '100%', minHeight: 360, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
            />
          ) : srcDoc ? (
            <iframe
              title="Appraisal Report"
              srcDoc={srcDoc}
              sandbox=""
              style={{ width: '100%', height: '100%', minHeight: 360, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
            />
          ) : (
            <div style={{ height: '100%', minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
              No report on file.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 18px', background: '#f3f4f6', color: '#111827', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HeroCtc — Clear-to-Close hero ───────────────────────────────────────
// Replaces HeroClosing for borrowers in stage 'clear_to_close'. Shows the
// celebratory copy, locked closing date, and the Complete E-Closing CTA.
// Wire-instructions card was intentionally dropped — the borrower already
// handled the wire during the closing stage (Send Cash to Close checklist
// item on HeroClosing), so re-surfacing the wire panel here would be
// duplicative + risk a double-wire.
function HeroCtc({ name, token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/closing/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const firstName = name ? name.split(' ')[0] : '';
  const d = data || {};
  const fmtClosingDate = (v) => {
    if (!v) return null;
    const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
    const [y, m, day] = s.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const closingDate = fmtClosingDate(d.closing_date);

  // E-closing — for now this opens the admin-set link if present, otherwise
  // it shows a polite "we'll guide you" toast. TODO: wire to the real
  // e-closing platform when that integration ships.
  const handleCompleteEClosing = () => {
    if (d.eclosing_url) {
      window.open(d.eclosing_url, '_blank', 'noopener');
    } else {
      alert("Your e-closing documents are being prepared. Our team will email you a link to sign within 24 hours of your closing date. Reach support@godoorly.com with any questions.");
    }
  };

  if (loading) {
    return <div className="mb-8 h-48 bg-gray-100 rounded-2xl animate-pulse" />;
  }

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 16,
      padding: 32,
      marginBottom: 24,
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
        🎉 You're Clear to Close{firstName ? `, ${firstName}` : ''}!
      </div>
      <div style={{ fontSize: 15, color: '#111827', marginBottom: 22, lineHeight: 1.6 }}>
        Everything is reviewed, verified, and confirmed. Your closing date is locked in.
      </div>

      {/* Closing date tile */}
      {closingDate && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.06, fontWeight: 700, marginBottom: 4 }}>
            Closing Date
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{closingDate}</div>
        </div>
      )}

      <button onClick={handleCompleteEClosing} type="button"
        style={{
          background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10,
          padding: '14px 28px', fontSize: 16, fontWeight: 800, cursor: 'pointer',
          width: '100%', marginBottom: 8,
        }}>
        Complete E-Closing →
      </button>
      <button onClick={() => navigate('/documents')} type="button"
        style={{
          background: 'transparent', color: '#374151',
          border: '1px solid #e5e7eb', borderRadius: 10,
          padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          width: '100%',
        }}>
        Review Closing Documents
      </button>
    </div>
  );
}

// ─── SmartStart Dashboard Card ─────────────────────────────────────────
// Renders inline on the borrower dashboard when the borrower is enrolled
// in SmartStart™. Three visual states:
//   - Locked    (enrolled, not yet unlocked) — muted, no CTA
//   - Active    (unlocked, in progress)      — yellow left border, progress bar, CTA
//   - Complete  (all 6 modules done)         — green border + certificate link
function SmartStartDashboardCard({ token }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/smartstart/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  // Authenticated certificate viewer. The /certificate endpoint requires a
  // Bearer JWT, so a plain <a target="_blank"> request fails with
  // "Authentication required" — the new tab can't carry the header.
  // Fetch as a blob with axios + the auth header, then open the blob URL.
  //
  // With responseType: 'blob', error bodies come back as Blobs too, so
  // err.response?.data?.error is undefined and we'd fall through to a
  // generic alert. Read the blob as text to surface the real server
  // message (same pattern as AdminBorrowerProfile.jsx#blobErrorMessage).
  const handleViewCertificate = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/smartstart/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = err?.message || 'Unable to open certificate. Please try again.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = (() => { try { return JSON.parse(text); } catch (_) { return null; } })();
          msg = parsed?.error || text || msg;
        } catch (_) { /* fall through to err.message */ }
      } else if (data?.error) {
        msg = data.error;
      }
      console.error('[SmartStart] View Certificate failed:', err?.response?.status, msg);
      alert(msg);
    }
  };

  if (loading || !status?.enrolled) return null;

  const completed = !!status.completedAt;
  const locked = !status.unlockedAt;
  const completedCount = status.completedCount || 0;
  const totalModules = status.totalModules || 6;
  const pct = Math.round((completedCount / totalModules) * 100);

  const cardBase = {
    background: '#fff', borderRadius: 16, padding: 20, marginBottom: 24,
    border: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
  };

  if (completed) {
    return (
      <div className="mb-6" style={{ ...cardBase, borderLeft: '4px solid #acf44e' }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f0fde4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>✅</span>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111111' }}>
            SmartStart<sup style={{ fontSize: '0.6em' }}>™</sup> Complete
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Homebuyer Success Program completed {new Date(status.completedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <button type="button" onClick={handleViewCertificate}
          style={{ padding: '8px 14px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfcff8', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          View Certificate →
        </button>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="mb-6" style={{ ...cardBase, opacity: 0.85 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 20 }}>📚</span>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111111' }}>
            SmartStart<sup style={{ fontSize: '0.6em' }}>™</sup> Homebuyer Program
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            Enrolled — unlocks once you accept your SmartEstimate. Completion required before closing.
          </div>
        </div>
        <button onClick={() => navigate('/smartstart')} type="button"
          style={{ padding: '8px 14px', background: 'none', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          Learn More
        </button>
      </div>
    );
  }

  // Active / unlocked / not yet complete
  return (
    <div className="mb-6" style={{ ...cardBase, borderLeft: '4px solid #ffdd86' }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: '#fffbef', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 20 }}>📚</span>
      </div>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111111' }}>
          SmartStart<sup style={{ fontSize: '0.6em' }}>™</sup> — Action Required
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, marginBottom: 8 }}>
          Complete your Homebuyer Success Program before closing — {completedCount} of {totalModules} modules done.
        </div>
        <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: '#acf44e', transition: 'width 0.3s ease' }} />
        </div>
      </div>
      <button onClick={() => navigate('/smartstart')} type="button"
        style={{ padding: '10px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
        Continue SmartStart →
      </button>
    </div>
  );
}

function HeroActiveLate({ stage, name, isCoBorrower, primaryName, token, propertyAddress }) {
  const navigate = useNavigate();
  const [cdRecord, setCdRecord] = useState(null);

  // Closing-stage only: surface the Closing Disclosure sign CTA when a
  // DocMagic CD package has been generated but isn't yet fully signed.
  useEffect(() => {
    if (stage !== 'closing' || !token) return;
    axios.get(`${API_URL}/api/disclosures/borrower-docs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const cd = (r.data?.docs || []).find((d) => d.doc_type === 'cd');
        if (cd) setCdRecord(cd);
      })
      .catch(() => { /* non-fatal */ });
  }, [stage, token]);
  const stageLabels = {
  offer_requested: { label: 'Purchase Started', color: 'amber', msg: "Our team is working on preparing the offer and will submit it within 1 business day. In the meantime, you can continue browsing homes." },
  home_shopping: { label: 'Home Shopping', color: 'cyan', msg: "Browse and find the right home. Once you request an offer, your Doorly team takes it from there." },
  under_contract: { label: 'Under Contract', color: 'indigo', msg: "Great news \u2014 you're under contract! Your Doorly team is coordinating with the title company." },
  closing: { label: 'Closing', color: 'blue', msg: "You're almost there! Review your closing disclosure and prepare for your closing date." },
  closed: { label: 'Loan Closed', color: 'green', msg: 'Your loan is closed. Welcome home! Reach out if you need anything.' },
  };
  const info = { ...(stageLabels[stage] ?? { label: stage, color: 'blue', msg: '' }) };
  // Co-borrower sees the same banner with a message reframed for their (read-only) role.
  if (isCoBorrower) {
    if (stage === 'closing') {
      info.msg = primaryName
        ? `Your file is in closing. ${primaryName} is coordinating final documents with your loan team.`
        : 'Your file is in closing. Final documents are being prepared by the primary borrower and your loan team.';
    } else if (stage === 'under_contract') {
      info.msg = primaryName
        ? `You're under contract! ${primaryName} is coordinating with the title company.`
        : "You're under contract! The primary borrower is coordinating with the title company.";
    }
  }
  const colorMap = {
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-100', iconText: 'text-amber-600', label: 'text-amber-600' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', iconBg: 'bg-cyan-100', iconText: 'text-cyan-600', label: 'text-cyan-600' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', label: 'text-indigo-600' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-[#eff3fe]', iconText: 'text-[#3a6ad9]', label: 'text-[#3a6ad9]' },
  green: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-100', iconText: 'text-green-600', label: 'text-green-600' },
  };
  const c = colorMap[info.color];
  return (
  <div className={`bg-gradient-to-br ${c.bg} to-white rounded-2xl border ${c.border} shadow-sm p-5 sm:p-8 mb-8`}>
  <div className="flex items-start gap-4">
  <div className={`w-12 h-12 rounded-2xl ${c.iconBg} flex items-center justify-center flex-shrink-0`}>
  <svg className={`w-6 h-6 ${c.iconText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </div>
  <div className="flex-1 min-w-0">
  <p className={`text-sm font-semibold ${c.label} uppercase tracking-wider mb-1`}>{info.label}</p>
  <h1 className="text-2xl sm:text-3xl font-bold text-black">
  {stage === 'closed'
    ? `Welcome home${name ? `, ${name.split(' ')[0]}` : ''}!`
    : stage === 'offer_requested'
      // ⚠️ THE ADDRESS ANSWERS THE ACTUAL QUESTION. "We've received your offer
      // request" is generic where the borrower wants to know whether it is
      // THEIR house being worked on. Falls back when no address is on the
      // record — offer_requested is reachable from the manual-submission path
      // where only a partial address exists.
      ? (propertyAddress
        ? `We've started work on ${propertyAddress}${name ? `, ${name.split(' ')[0]}` : ''}`
        : `We've started work on your purchase${name ? `, ${name.split(' ')[0]}` : ''}`)
      : `You're in the ${info.label} stage${name ? `, ${name.split(' ')[0]}` : ''}`}
  </h1>
  {info.msg && <p className="text-gray-500 mt-2 leading-relaxed">{info.msg}</p>}
  {stage === 'offer_requested' && (
  <button
    onClick={() => navigate('/homes')}
    className="mt-5 inline-flex items-center gap-2.5 font-bold text-sm text-white rounded-full transition-opacity hover:opacity-90"
    style={{ backgroundColor: '#3a6ad9', padding: '10px 8px 10px 20px' }}
  >
    Continue Browsing Homes
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, backgroundColor: '#fffd5b', borderRadius: '50%', flexShrink: 0 }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2 7H12M12 7L7 2M12 7L7 12" stroke="#1e3a8a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  </button>
  )}

  {/* 🔑 The "Good to know" box is gone (the redesign, 2026-11-02): its approved sentence (item 10) is in the main card's
      light-blue panel (components/dashboard/JourneyMainCard), "down payment" as plain text. */}

  {/* Closing stage: Closing Disclosure sign CTA */}
  {stage === 'closing' && cdRecord && cdRecord.package_status !== 'completed' && !cdRecord.all_signed_at && (
    <div style={{
      background: '#f0f4ff',
      border: '2px solid #3a6ad9',
      borderRadius: 12,
      padding: 24,
      marginTop: 16,
    }}>
      <h3 style={{ color: '#3a6ad9', margin: '0 0 8px 0' }}>
        Your Closing Disclosure is Ready
      </h3>
      <p style={{ margin: '0 0 16px 0', color: '#374151' }}>
        Please review and e-sign your Closing Disclosure to proceed to closing.
      </p>
      <button
        onClick={() => navigate('/documents')}
        style={{
          background: '#3a6ad9',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          cursor: 'pointer',
          fontSize: 15,
          fontWeight: 600,
        }}>
        Review &amp; Sign Documents →
      </button>
    </div>
  )}
  {stage === 'closing' && cdRecord && cdRecord.all_signed_at && (
    <div style={{ color: '#16a34a', fontWeight: 600, marginTop: 16 }}>
      ✓ Your Closing Disclosure has been signed
    </div>
  )}
  </div>
  </div>
  </div>
  );
}

// ── Minimum down payment fell ──────────────────────────────────────────────
// 🚨 A STRIP, NOT A HERO, AND THAT IS THE WHOLE PLACEMENT ARGUMENT. This fires at
// Phase 3, when the hero below is driving active work — documents outstanding,
// needs to answer. A down-payment change is NEWS, not a task, and displacing a
// task hero with news stalls the thing we want the borrower doing. Above the
// hero, dismissible, out of the way.
//
// ⚠️ NOT ON /apply EITHER. The recompute happens after the credit pull, and the
// application is submitted and behind them by then — re-rendering a number on a
// page they have left is a change they would never see.
//
// 🚨 SHOWN EVEN WHEN THEIR ENTERED DOWN PAYMENT ALREADY CLEARS THE NEW MINIMUM.
// A borrower who entered $70,000 against a $52,500 floor may have chosen that
// BECAUSE $52,500 felt close to the edge — a choice made on a number that turned
// out to be wrong. Reserves or closing costs may matter more to them than a
// smaller loan, and that is theirs to weigh. Hiding it because they "do not need
// it" decides for them.
//
// ⚠️ ONLY THE DOWNWARD DIRECTION REACHES HERE. The server marks which moves are
// tellable; a floor that rose is an ECOA counteroffer and goes to staff through
// the adverse-action path, never to a dashboard strip.
// ── "Having trouble reaching your agent?" ──────────────────────────────────
// 🚨 HIDDEN, NOT DISABLED, FOR A BORROWER WITH NO AGENT. A self-registered
// borrower never had one — a control saying "you don't have an agent" answers a
// question they did not ask and invites them to wonder whether they were supposed
// to have one. applicable:false renders nothing at all.
//
// ⚠️ THE ANSWER COMES FROM GET /api/borrower/agent-unreachable, which asks
// referringPartnerFor → utils/borrowerArrival.js. It is the same arrival question
// /homes asks to hide Schedule Showing; a separate derivation here would be the
// fourth, and they would agree today and disagree silently later.
function AgentTroubleLink({ token }) {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/borrower/agent-unreachable`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);
  if (!info || !info.applicable) return null;
  return (
    <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7280' }}>
      <a href="/agent-unreachable" style={{ color: '#3a6ad9', fontWeight: 600, textDecoration: 'none' }}>
        Having trouble reaching your agent?
      </a>
    </div>
  );
}

function MinDownPaymentStrip({ token }) {
  const [rec, setRec] = React.useState(null);
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/readycheck/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setRec(r.data?.minDpRecompute || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  if (!rec || rec.afterAmount == null || rec.beforeAmount == null) return null;

  const money = (n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const dismiss = () => {
    setRec(null);
    axios.post(`${API_URL}/api/readycheck/acknowledge-min-dp`, {},
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  return (
    <div style={{
      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14,
      padding: '16px 20px', marginBottom: 20, display: 'flex',
      alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#14532d', marginBottom: 6 }}>
          Your verified credit score lowered your minimum down payment.
        </div>
        <p style={{ fontSize: 13, color: '#166534', lineHeight: 1.6, margin: '0 0 8px' }}>
          Your credit report came back at {rec.verifiedScore}. At that score, Doorly&rsquo;s program
          requires a minimum of <strong>{money(rec.afterAmount)}</strong>
          {rec.price ? ` on a ${money(rec.price)} purchase` : ''} — down from the{' '}
          {money(rec.beforeAmount)} estimated when you applied, which was based on the credit range
          you selected before we had your report.
        </p>
        <p style={{ fontSize: 13, color: '#166534', lineHeight: 1.6, margin: 0 }}>
          This is a change to the <em>minimum</em>, not to your approval or your pricing. If
          you&rsquo;ve planned around the higher figure, you can still put down more — a larger down
          payment lowers your loan amount and your monthly payment.
        </p>
      </div>
      <button onClick={dismiss} style={{
        background: 'transparent', border: '1px solid #bbf7d0', color: '#166534',
        borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600,
        cursor: 'pointer', flexShrink: 0,
      }}>Got it</button>
    </div>
  );
}

// ── Your broker sent us an application ─────────────────────────────────────
// 🚨 THE ONLY IN-APP DOOR TO /broker-application, WHICH PREVIOUSLY HAD NONE. The
// route was declared in App.js and referenced nowhere else in the frontend — no
// link, no redirect, no prompt. A borrower could register, land here, and never
// learn that their entire application was sitting prefilled behind a URL they had
// no way to know about, while the submission expired underneath them.
//
// ⚠️ A HERO, NOT A REDIRECT, AND THAT IS A DELIBERATE CHOICE. A redirect fights
// whatever the borrower came to do and has no honest "not now" — dismissed, it
// has nowhere to go, and on the second login it either hijacks them again or
// silently stops, which is the worst of both. The hero slot is also not being
// taken from anything: at this point their application_status is not_started, so
// what it replaces is HeroNoApp — "start your application" — which is strictly
// worse advice than "the application your broker already filled in is waiting".
//
// ⚠️ AND IT CARRIES THE DEADLINE, which is the argument for a redirect answered
// without a redirect. The file is deleted on expiry, so the count of days left is
// the urgency, and a hero can repeat it on every visit where a redirect says it
// once.
function HeroBrokerSubmission({ token, onDismiss }) {
  const [sub, setSub] = React.useState(null);
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/borrower/submission`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled && r.data && r.data.found) setSub(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);
  React.useEffect(() => { if (onDismiss) onDismiss(!!sub); }, [sub, onDismiss]);
  if (!sub) return null;

  // 🚨 `found` NO LONGER MEANS "UNFINISHED". The submission is linked at
  // registration and stays linked, so this hero renders on next_step — which
  // the server sets only while the borrower is still at the registered step.
  // Without it, somebody who submitted months ago would still be told their
  // application is waiting for them.
  if (!sub.next_step) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 mb-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#3a6ad9] mb-2">
        Waiting for you
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">
        {/* 🚨 Names nobody — no person, role or firm (Jesse, 2026-10-28). */}
        An application was sent to us for you
      </h1>
      {/* ── 🚨 THE WORDS AND THE ROUTE ARE THE SERVER'S ──────────────────────
          This hero said "nothing happens with it until you review it" and sent
          the borrower to a screen where they confirmed their broker's answers
          field by field — then on to /apply to enter the same values again.
          There is no review screen now: the submission is linked to their
          account at registration and the application is already filled in, so
          the sentence and the button both come from the journey step they are
          actually on, which the dashboard, Eva and this hero all read.

          ⚠️ AND THE DEADLINE SENTENCE IS GONE WITH IT. The 30-day sweep stopped
          applying the moment the file was linked, so a countdown here would be
          pressure about an expiry that cannot happen. */}
      <p className="text-sm text-gray-600 leading-relaxed mb-5 max-w-2xl">
        {sub.next_step.next_step}
      </p>
      <a href={sub.next_step.route}
        className="inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-semibold text-white"
        style={{ backgroundColor: '#3a6ad9' }}>
        {sub.next_step.cta_label}
      </a>
    </div>
  );
}

// 🔑 A NOTICE THAT WENT BY MAIL ONLY (Jesse, 2026-10-16): the PDF stays staff-only, and the
// borrower sees this line instead. The text and both dates come from the server, read off the
// mailing task (backend/utils/mailedNoticeStatus), so the portal and the paper cannot disagree.
function MailedNoticeStatus({ token }) {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/borrower/mailed-notices`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setLines(Array.isArray(r.data?.lines) ? r.data.lines : []); })
      .catch(() => { if (!cancelled) setLines([]); });
    return () => { cancelled = true; };
  }, [token]);
  if (!lines.length) return null;
  return (
    <div data-testid="mailed-notice-status" role="status"
         style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '12px 16px', margin: '0 0 16px', color: '#1e3a8a', fontSize: 14, lineHeight: 1.6 }}>
      {lines.map((l) => <p key={l.taskId} style={{ margin: 0 }}>{l.text}</p>)}
    </div>
  );
}

function DashboardHero({ status, loading, onAccepted, checklist, token, isCoBorrower, primaryName }) {
  if (loading) {
  return (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 mb-8 animate-pulse">
  <div className="h-5 bg-gray-200 rounded w-1/4 mb-3" />
  <div className="h-8 bg-gray-200 rounded w-2/3 mb-3" />
  <div className="h-4 bg-gray-100 rounded w-1/2" />
  </div>
  );
  }
  if (!status) return <HeroNoApp />;
  const { loan_stage, name } = status;
  // 🔑 BEFORE THE STEP-8 DECISION THE BORROWER IS IN THE GUIDED SHELL, NEVER HERE (components/guided/RequireGuidedFlow).
  // The classic pre-decision heroes (submitted, documents needed, in review, adverse pending, declined,
  // the classic approval) are gone (cleanup Phase 2, Jesse 2026-10-28/29): any of those states lands on the neutral
  // "your application" card, which goes back to the guided steps.
  if (status?.under_review === true) return <HeroUnderReview name={name} />;
  if (status?.is_adversed) return <HeroNoApp />;
  if (status?.is_archived) return <HeroPaused name={name} />;
  switch (loan_stage) {
  // 🚨 A BORROWER SIGNS ON STEP 8, NEVER HERE (the step-8 fold, 2026-10-22): one place to accept.
  case 'approved':
  case 'conditional_approval': return (
      <div data-testid="dashboard-sign-on-step8" className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 mb-8">
        <h1 className="text-xl font-bold text-black mb-2">You're approved</h1>
        <p className="text-gray-600 text-sm mb-4">Your SmartEstimate and program agreement are on step 8 of your application.</p>
        <a href="/apply/guided" style={{ display: 'inline-block', padding: '10px 18px', background: '#3a6ad9', color: '#fff', borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Go to step 8</a>
      </div>
  );
  case 'option_period': return <HeroOptionPeriod name={name} token={token} isCoBorrower={isCoBorrower} primaryName={primaryName} onStageReset={onAccepted} />;
  case 'offer_submitted': return <HeroOfferSubmitted name={name} />;
  case 'offer_accepted':  return <HeroOfferAccepted name={name} token={token} isCoBorrower={isCoBorrower} onStageReset={onAccepted} />;
  case 'closing':         return (
    // NC borrowers see HeroNewConstructionClosing through their under_contract
    // phase (combined contract+closing hero), but the moment they advance to
    // application_status='closing' they get the standard HeroClosing like
    // every other borrower — so the stage→hero mapping stays consistent and
    // closing-stage content (CTC wire, HOI, utility setup) renders the same
    // for everyone in 'closing'.
    <HeroClosing name={name} token={token} isCoBorrower={isCoBorrower} primaryName={primaryName} />
  );
  case 'clear_to_close':  return (
    <>
      <HeroCtc name={name} token={token} />
      <UtilitySetupCard />
    </>
  );
  // New construction lands in under_contract (task window: down payment + PSA)
  // before closing — show the combined hero in its under-contract phase. Any
  // non-NC under_contract (legacy/leads-only) keeps the generic active hero.
  case 'under_contract': return (
    status.is_new_construction
      ? <HeroNewConstructionClosing name={name} token={token} isCoBorrower={isCoBorrower} primaryName={primaryName} underContract={true} />
      : <HeroActiveLate stage={loan_stage} name={name} isCoBorrower={isCoBorrower} primaryName={primaryName} token={token} />
  );
  // 🔑 HOME SHOPPING'S MAIN CARD IS components/dashboard/JourneyMainCard (the redesign, 2026-11-02): the mint card is gone.
  case 'home_shopping': return null;
  case 'offer_requested': return <HeroActiveLate stage={loan_stage} name={name} isCoBorrower={isCoBorrower} primaryName={primaryName} token={token} propertyAddress={status?.property_address || null} />;
  case 'closed':
  case 'funded': return (
    <>
      <HeroClosed name={name} token={token} isCoBorrower={isCoBorrower} />
      <UtilitySetupCard />
    </>
  );
  // Terminal / paused files — neutral hold hero, never a live action hero. The
  // backend guard resolves these to themselves (not the old lead stage), so a
  // withdrawn/paused borrower can't be shown a down-payment/signing CTA.
  case 'not_moving_forward':
  case 'on_hold': return <HeroPaused name={name} />;
  default: return <HeroNoApp />;
  }
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard() {
  const { user, token } = useAuth();
  const { triggerSurvey } = useSurvey();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // Set by HeroBrokerSubmission once it knows whether a claimable submission
  // exists, so the normal hero stands down rather than both rendering.
  // ⚠️ DECLARED IN Dashboard(), NOT IN HeroSubmitted. This file has several
  // components with near-identical state blocks — the first attempt landed it in
  // the wrong one, parsed cleanly, and failed the build with no-undef.
  const [hasBrokerSubmission, setHasBrokerSubmission] = useState(false);
  const [employment, setEmployment] = useState('');
  const [finishModal, setFinishModal] = useState(!!location.state?.showFinishModal);
  // Offer-rejected login popup. Surfaced when (a) backend /status returns
  // an offer_rejected_popup payload and (b) the borrower hasn't yet
  // dismissed it. PATCH /api/borrower/offer-rejected-popup-dismissed
  // stamps the dismissal so the modal stops firing on subsequent loads.
  const [offerRejectedDismissing, setOfferRejectedDismissing] = useState(false);
  const [calcModal, setCalcModal] = useState(false);
  const [coborrowerStatus, setCoborrowerStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [resumeTransactionId, setResumeTransactionId] = useState(null);

  const loanStage = status?.loan_stage || null;

  // Handle Stripe redirect returns. Two paths land here:
  //   1. payment_intent=pi_xxx — bank-redirect payment methods bouncing
  //      back from 3DS / ACH verification (the legacy path).
  //   2. checkout_session_id=cs_xxx — the Stripe-hosted Checkout
  //      fallback path used when the embedded PaymentElement iframe
  //      can't mount in the borrower's browser (content blocker /
  //      mobile Safari ITP / corporate firewall). Completes the
  //      offer creation server-roundtrip from here since the modal
  //      was never the one driving the payment.
  // ── 🔑 ARRIVING FROM THE GUIDED SHELL'S STEP 8 (phase 6 item 2) ───────────
  //
  // Jesse: "'See your SmartEstimate' → exits the shell to the dashboard with
  // SmartEstimate open."
  //
  // ⚠️ THE PARAM IS STRIPPED AFTER IT IS USED, like the checkout params below.
  // A borrower who bookmarks the URL or refreshes should not be scrolled again
  // every time, and a param that outlives its effect is state in the address bar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('smartestimate') !== '1') return;
    // The hero mounts after the status fetch resolves, so the scroll waits for
    // the element rather than firing into an empty page.
    let tries = 0;
    const tick = setInterval(() => {
      const el = document.getElementById('smart-estimate');
      tries += 1;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState({}, '', '/dashboard');
        clearInterval(tick);
      } else if (tries > 40) {
        window.history.replaceState({}, '', '/dashboard');
        clearInterval(tick);
      }
    }, 150);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutSessionId = params.get('checkout_session_id');
    const paymentComplete   = params.get('payment_complete') === '1';
    const checkoutCanceled  = params.get('checkout_canceled') === '1';

    if (checkoutCanceled) {
      // Don't fire any side effects — borrower bailed out of Checkout.
      // Just strip the param so the URL is clean on the next nav.
      window.history.replaceState({}, '', '/dashboard');
      return;
    }

    if (paymentComplete && checkoutSessionId && token) {
      // Stripe Checkout fallback return path.
      (async () => {
        try {
          const confirmRes = await fetch(`${API_URL}/api/auth/pay/checkout-confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ checkout_session_id: checkoutSessionId }),
          });
          const confirmData = await confirmRes.json();
          if (!confirmData.success) {
            console.error('[Checkout Return] confirm failed:', confirmData.error);
            return;
          }
          const intentId = confirmData.payment_intent_id;
          const meta     = confirmData.metadata || {};
          const ptype    = confirmData.payment_type;

          // Run the same post-payment side effects /pay/confirm fires
          // on the normal PaymentElement path (offer_fee_lender_credit_
          // applied flag, application_event log, admin alert, confirmation
          // email). Server-side idempotent on payment_transactions.
          await fetch(`${API_URL}/api/auth/pay/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ payment_intent_id: intentId, payment_type: ptype, showing_id: meta.showing_id }),
          }).catch(() => {});

          // If this was an offer flow, create the offer record now.
          // Mirrors what Homes.js / HomeDetail.js does after a normal
          // PaymentModal success.
          if (ptype === 'offer_fee' && meta.listing_id && meta.address) {
            await fetch(`${API_URL}/api/homes/offer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                listing_id: meta.listing_id,
                address:    meta.address,
                notes:      meta.notes || '',
                payment_intent_id: intentId,
                offer_initiation_payment_method: 'stripe_checkout',
              }),
            }).catch((e) => console.error('[Checkout Return] offer create failed:', e.message));
          }
        } catch (e) {
          console.error('[Checkout Return]', e.message);
        } finally {
          window.history.replaceState({}, '', '/dashboard');
        }
      })();
      return;
    }

    if (paymentComplete) {
      const intentId = params.get('payment_intent');
      if (intentId && token) {
        fetch(`${API_URL}/api/auth/pay/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ payment_intent_id: intentId }),
        }).catch(() => {});
      }
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  if (!user?.email || !token) { setLoading(false); return; }

  const headers = { Authorization: `Bearer ${token}` };

  axios.get(`${API_URL}/api/applications/status`, { headers })
  .then(async (r) => {
  if (r.data.status !== 'completed') {
  // ── 🔑 THE BROKER-SUBMISSION SKIP IS GONE, AND SO IS THE EXTRA REQUEST ──
  //
  // This redirect used to be skipped for a borrower with a waiting submission,
  // because it sent them to step 1 of a BLANK application while the review
  // screen held everything their broker had typed. Both halves of that have
  // changed: the submission is linked to their account at registration, so
  // step 1 is already filled in, and there is no review screen to be sent to
  // instead. The redirect's destination is now the right destination for every
  // borrower, which is what "align the borrower journey with all other
  // borrowers" means in one line of code.
  // ── 🚨 NOT WHEN THE BORROWER HAS JUST ASKED TO LEAVE THE SHELL ──────────
  //
  // 🚨 THIS MADE "Save & exit" A TWO-HOP ROUND TRIP. The shell's exit navigates
  // here declaring fromGuided, RequireGuidedFlow honours that one navigation —
  // and then this redirect fired, sending the borrower to /apply?step=N with no
  // state, which the guard then routed straight back into the shell. The button
  // appeared to do nothing, one hop later than before.
  //
  // 🔑 AND FORWARDING THEM AT ALL WOULD BE WRONG HERE. They asked to leave the
  // eight steps; the right destination is the dashboard they chose, not an
  // application surface outside the shell.
  if (location.state && location.state.fromGuided) {
  setLoading(false);
  return;
  }
  // 🔑 An unfinished application is on the guided steps (the standalone /apply form is gone, cleanup Phase 2).
  navigate('/apply/guided', { replace: true });
  return;
  }
  // Fetch full borrower status and employment type in parallel
  try {
  const [borrower, appData] = await Promise.all([
  axios.get(`${API_URL}/api/borrower/status`, { headers: { Authorization: `Bearer ${token}` } }),
  axios.get(`${API_URL}/api/applications/my`, { headers }).catch(() => ({ data: { found: false } })),
  ]);
  setStatus(borrower.data);
  if (appData.data?.found) {
  setEmployment(appData.data.form_data?.employment_type || '');
  }
  } catch (err) {
  // 404 = no leads row yet (fresh signup before the lead is created).
  // Soft-fallback to "Application Received" so the user sees the
  // verification CTA. Any other error (5xx, network failure) silently
  // downgrading an approved borrower's view to "Application Received"
  // is misleading — log and let HeroNoApp render until the next poll
  // succeeds. Symptom that motivated this: an approved borrower briefly
  // saw HeroSubmitted on a transient /borrower/status failure.
  const httpStatus = err?.response?.status;
  if (httpStatus === 404) {
  setStatus({
  loan_stage: 'new',
  name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
  });
  } else {
  console.error('[Dashboard] /borrower/status fetch failed —',
  httpStatus || 'network', err?.response?.data?.error || err.message);
  setStatus(null);
  }
  }
  })
  .catch(() => setStatus(null))
  .finally(() => setLoading(false));
  // ⚠️ location.state IS A DEPENDENCY because the effect reads it to decide
  // whether to forward a borrower who has just left the guided shell. Listing
  // it rather than silencing the rule: the value is set once per navigation, so
  // naming it cannot re-fire this effect in a loop.
  }, [user, token, navigate, location.state]);

  // Poll borrower status every 30s so admin stage changes propagate without a refresh
  useEffect(() => {
  if (!user?.email || !token) return;
  const poll = () => {
    axios.get(`${API_URL}/api/borrower/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(prev => {
        if (prev?.loan_stage !== r.data.loan_stage) return r.data;
        return prev;
      }))
      .catch(() => {});
  };
  const id = setInterval(poll, 30000);
  return () => clearInterval(id);
  }, [user?.email, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch co-borrower connection status
  useEffect(() => {
  if (!token) return;
  axios.get(`${API_URL}/api/coborrower/status`, { headers: { Authorization: `Bearer ${token}` } })
  .then((r) => setCoborrowerStatus(r.data))
  .catch(() => {});
  }, [token]);

  // Redirect to portal pages when borrower is in under_contract or option_period stage
  useEffect(() => {
  if (!loanStage) return;
  // New-construction borrowers skip the option period, so they stay on the
  // dashboard and see the in-dashboard HeroNewConstructionClosing (under-contract
  // phase) — NOT the standalone /portal/under-contract page. Non-NC under_contract
  // (legacy) keeps the portal redirect.
  if (loanStage === 'under_contract' && !status?.is_new_construction) navigate('/portal/under-contract', { replace: true });
  // loan_docs deliberately NOT routed: /portal/loan-docs is retired (redirects to
  // /dashboard), so routing here caused an infinite /dashboard↔/portal/loan-docs
  // loop. A loan_docs borrower now renders the dashboard normally. The signature
  // it used to collect (Owner Finance Contract = B-C PSA) is signed at option_period.
  if (loanStage === 'closing_disclosures') navigate('/portal/closing-disclosures', { replace: true });
  if (loanStage === 'final_review') navigate('/portal/final-review', { replace: true });
  }, [loanStage, status?.is_new_construction, navigate]);

  useEffect(() => {
  if (!status) return;
  if (status.loan_stage === 'closed') {
    setTimeout(() => triggerSurvey('post_closing'), 2000);
  }
  }, [status?.loan_stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 THE APPLICATION SURVEY FOR A GUIDED BORROWER (Jesse, 2026-10-19): once, the first time they land on the dashboard
  // after an APPROVED step-8 decision (only an approval stamps the shell's completedAt). Never during steps 1–8 (the
  // shell does not ask), never on the decline screen (a decline stays in the shell). "Once" is the server's:
  // post_application is shown at most once per borrower (routes/surveys.js ONCE_ONLY).
  useEffect(() => {
  if (!token) return undefined;
  let cancelled = false;
  let surveyTimer = null;
  axios.get(`${API_URL}/api/guided-flow/state`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      // 🔑 THE CO-BORROWER TOO (Joint Part 2 D, Jesse 2026-10-27): their shell is never stamped complete (the primary's
      // signature closes it), so an approved borrower who is out of the shell counts as well. Once per user (surveys_shown).
      if (cancelled || !(r.data && r.data.enabled && r.data.approved && !r.data.declined && (r.data.completedAt || !r.data.shouldEnter))) return;
      // 🔑 NEVER STACKED (Jesse, 2026-10-22 — the step-8 fold, decision 6): it opens only once no other modal is on
      // the page (the approval celebration, an inspection / insurance dialog, anything with role=dialog or a fixed
      // full-screen overlay). Checked every second; given up after two minutes — the next landing tries again,
      // since only a shown survey is recorded.
      const otherModalOpen = () => !!document.querySelector('[role="dialog"], [aria-modal="true"], .fixed.inset-0');
      let tries = 0;
      const tick = () => {
        if (cancelled) return;
        if (otherModalOpen()) { if ((tries += 1) < 120) surveyTimer = setTimeout(tick, 1000); return; }
        triggerSurvey('post_application');
      };
      surveyTimer = setTimeout(tick, 2000);
    })
    .catch(() => {});
  return () => { cancelled = true; clearTimeout(surveyTimer); };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 🚨 THE GOOGLE ADS CONVERSION USED TO FIRE HERE. IT CANNOT. ───────────
  //
  // This is the authenticated borrower dashboard, keyed to one person's loan,
  // and it fired on loan_stage — reading the file's state to decide whether to
  // call Google. Google Ads no longer loads on any authenticated route, so the
  // tag is not in the document on this page and `window.gtag` is undefined.
  //
  // 🔑 IT MOVED TO THE LAST PUBLIC STEP — pages/PreQualification.js, just
  // before it forwards to /register. See utils/marketingTag.js CONVERSIONS.
  //
  // ⚠️ AND IT COUNTS A DIFFERENT EVENT NOW, which is a measurement change
  // rather than a refactor: pre-qualification completed fires EARLIER and for
  // MORE people than "reached the dashboard" or "submitted a ReadyCheck".
  // Recorded in docs/follow-ups.md with the server-side alternative, because
  // the accurate version of this number cannot come from a browser tag on a
  // page the tag is not allowed on.
  //
  // 🔑 THE localStorage KEY IS UNCHANGED ('gads_conversion_fired'), so a
  // device that already fired does not fire again at the new location.


  // Fetch payment history
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/auth/my-payments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPayments(r.data.transactions || []))
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAccepted = () => {
  // Re-fetch borrower status so hero transitions to home_shopping
  axios
  .get(`${API_URL}/api/borrower/status`, { headers: { Authorization: `Bearer ${token}` } })
  .then((r) => setStatus(r.data))
  .catch(() => {});
  };

  return (
  <AppShell layout="guided">
  {/* 🔑 THE GUIDED FLOW'S LOOK (the Home Shopping page redesign, Jesse/Hannah 2026-11-02): the grey page, the guided
      column, "Welcome back" and the five-step line and bar (AppShell → components/JourneyHeader), one white main card per
      stage (components/dashboard/JourneyMainCard), white cards for the rest — light blue only for the one thing waiting
      on the borrower. Gone: the mint Home Shopping card, Quick Actions, the yellow "Good to know" box (its sentence is in
      the main card) and the showing-credits bar (item 8). */}
  <div className="px-4 sm:px-6" style={{ paddingTop: 16 }}>
  <div data-testid="dashboard-column" style={guidedColumn}>
  {/* 🔑 The approval celebration is step 8's, shown once before signing (the fold, 2026-10-22) — never here. */}
  {/* Down-payment minimum fell — above the hero, never instead of it. */}
  <MinDownPaymentStrip token={token} />

  {/* Renders nothing for a borrower with no agent on file. */}
  <AgentTroubleLink token={token} />

  {/* ⚠️ THE BROKER SUBMISSION TAKES THE HERO SLOT, BUT NEVER FROM FRAUD REVIEW.
      That overlay is pre-decision and highest precedence by design (see
      DashboardHero) — a borrower under review must see the neutral review notice,
      not a prompt to ratify a file. Everything else the normal hero would say at
      this stage is "start your application", which is worse advice than "the one
      your broker already filled in is waiting". */}
  {status?.under_review !== true && (
    <HeroBrokerSubmission token={token} onDismiss={setHasBrokerSubmission} />
  )}

  {/* State-driven hero */}
  <MailedNoticeStatus token={token} />
  {!(hasBrokerSubmission && status?.under_review !== true) && !loading && (
    <div className="mb-4">
      <JourneyMainCard status={status} onBrowse={() => navigate('/homes')} onViewSmartEstimate={() => setCalcModal(true)} />
    </div>
  )}
  {!(hasBrokerSubmission && status?.under_review !== true) && <DashboardHero status={status} loading={loading} onAccepted={handleAccepted} checklist={getDocChecklist(employment)} token={token} isCoBorrower={coborrowerStatus?.role === 'coborrower'} primaryName={coborrowerStatus?.primary_first_name} />}

  {/* Additional Needs panel — top-level so it fires for any borrower
      with open needs, regardless of which hero is rendering above.
      Self-fetches /api/review/sections and only renders when there's
      something non-resolved on the wire. */}
  <DashboardOpenNeedsPanel token={token} />

  {/* 🔑 "Your application" ("Request a change" after the decision) — at Home Shopping ONLY (the redesign, 2026-11-02).
      Once an offer is requested it is gone from the page and the server refuses a request (utils/applicationEdits). */}
  {loanStage === 'home_shopping' && <EditApplicationCard token={token} />}

  {/* SmartStart card — renders only for borrowers enrolled in
      SmartStart. Surfaces locked / active / complete state inline.
      Positioned directly under the hero (above Quick Actions) so
      the closing-gate compliance step has top-of-fold prominence
      for the borrowers it applies to. SmartStartDashboardCard
      returns null when status?.enrolled is false, so this doesn't
      add visual weight for borrowers without it. */}
  <SmartStartDashboardCard token={token} />

  {/* 🔑 No showing-credits bar here (Hannah's review, Jesse 2026-11-02 item 8): it lives on the Homes page only. */}

  {/* Co-Borrower Status Card */}
  {coborrowerStatus && coborrowerStatus.role === 'primary' && coborrowerStatus.status !== null && (
  <div className="mb-8">
  {coborrowerStatus.status === 'pending' && (
  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 flex items-start gap-4">
  <span className="text-xl flex-shrink-0">⏳</span>
  <div className="flex-1 min-w-0">
  <p className="text-sm font-bold text-yellow-900">Co-Borrower Invited</p>
  <p className="text-sm text-yellow-700 mt-0.5">{coborrowerStatus.coborrower_first_name} {coborrowerStatus.coborrower_last_name} has been invited but has not yet registered.</p>
  </div>
  <button
  onClick={() => axios.post(`${API_URL}/api/coborrower/resend/${coborrowerStatus.connection_id}`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {})}
  className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border border-yellow-400 text-yellow-800 hover:bg-yellow-100 transition-colors"
  >Resend</button>
  </div>
  )}
  {coborrowerStatus.status === 'expired' && (
  <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-4">
  <span className="text-xl flex-shrink-0">⚠️</span>
  <div className="flex-1 min-w-0">
  <p className="text-sm font-bold text-red-900">Co-Borrower Invitation Expired</p>
  <p className="text-sm text-red-700 mt-0.5">{coborrowerStatus.coborrower_first_name} {coborrowerStatus.coborrower_last_name}'s invitation has expired.</p>
  </div>
  <button
  onClick={() => axios.post(`${API_URL}/api/coborrower/resend/${coborrowerStatus.connection_id}`, {}, { headers: { Authorization: `Bearer ${token}` } }).then(() => setCoborrowerStatus((p) => ({ ...p, status: 'pending' }))).catch(() => {})}
  className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full text-white transition-colors"
  style={{ backgroundColor: '#3a6ad9' }}
  >Resend Invitation</button>
  </div>
  )}
  {coborrowerStatus.status === 'accepted' && (
  <div className="rounded-2xl p-5 flex items-start gap-4" style={{ backgroundColor: '#eff3fe', border: '1px solid #dbe4ff' }}>
  <span className="text-xl flex-shrink-0">👥</span>
  <div className="flex-1 min-w-0">
  <p className="text-sm font-bold" style={{ color: '#1e3a8a' }}>Co-Borrower: {coborrowerStatus.coborrower_first_name} {coborrowerStatus.coborrower_last_name}</p>
  <p className="text-sm mt-0.5" style={{ color: '#3a6ad9' }}>Application linked to your loan file.</p>
  </div>
  </div>
  )}
  </div>
  )}
  {coborrowerStatus && coborrowerStatus.role === 'coborrower' && (
  <div className="mb-8 rounded-2xl p-5 flex items-start gap-4" style={{ backgroundColor: '#eff3fe', border: '1px solid #dbe4ff' }}>
  <span className="text-xl flex-shrink-0">🏠</span>
  <div>
  <p className="text-sm font-bold" style={{ color: '#1e3a8a' }}>Linked Loan</p>
  <p className="text-sm mt-0.5" style={{ color: '#3a6ad9' }}>You are a co-borrower with <strong>{coborrowerStatus.primary_first_name} {coborrowerStatus.primary_last_name}</strong>. Both applications are linked to the same loan file.</p>
  </div>
  </div>
  )}

  {/* Have questions? */}
  <div className="mb-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
  <div className="flex items-start gap-4">
  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eff3fe' }}>
  <svg className="w-5 h-5 text-[#3a6ad9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </div>
  <div>
  <h3 className="text-sm font-bold text-black mb-1">Have questions?</h3>
  {/* 🔑 Approved wording (2026-11-02): "Chat with us anytime, or visit the Help Center." — "Help Center" is the link. */}
  <p data-testid="dashboard-questions" className="text-sm text-gray-500">
  Chat with us anytime, or visit the{' '}
  {/* The help centre is MOVING FROM INTERCOM TO A NATIVE BUILD AT THIS SAME
    SUBDOMAIN. Do not remove this link during Intercom cleanup — the destination
    changes underneath it, the URL does not. See
    backend/knowledge/docs/help-export/MIGRATION.md for the cutover order. */}
  <a
  href="https://help.godoorly.com"
  target="_blank"
  rel="noreferrer"
  className="text-[#3a6ad9] hover:underline font-medium"
  >
  Help Center
  </a>.
  </p>
  </div>
  </div>
  </div>

  {/* ── Offer Rejected login popup ── */}
  {/* Cannot be dismissed by clicking outside. Two paths consume the
      dismissal: "Browse Homes" navigates + dismisses; "I'll do this later"
      just dismisses. PATCH stamps offer_rejected_popup_dismissed so the
      modal won't fire again. Reason copy mirrors the borrower email. */}
  {status?.offer_rejected_popup && (() => {
    const reason = String(status.offer_rejected_popup.reason || '').toLowerCase();
    const body = (reason.includes('listing agent accepted') || reason.includes('seller rejected'))
      ? 'Unfortunately, the seller accepted another offer on the home you selected. This happens — and we\'re here to help you find the right home.'
      : reason.includes('withdrawn by doorly')
        ? 'Doorly has withdrawn the offer on your selected property. A team member will reach out to you shortly.'
        : 'Your offer on the selected property did not move forward. We\'re ready to help you find your next home.';
    const dismiss = async (navigateAfter) => {
      setOfferRejectedDismissing(true);
      try {
        await axios.patch(`${API_URL}/api/borrower/offer-rejected-popup-dismissed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_) { /* non-fatal — clear local state regardless */ }
      setStatus(prev => prev ? { ...prev, offer_rejected_popup: null } : prev);
      setOfferRejectedDismissing(false);
      if (navigateAfter) navigate('/homes');
    };
    return (
      <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-7">
          <div className="text-center mb-2" style={{ fontSize: 36 }}>🏠</div>
          <h2 className="text-xl font-bold text-center mb-3" style={{ color: '#111827' }}>Your offer did not move forward</h2>
          <p className="text-sm text-center mb-5" style={{ color: '#374151', lineHeight: 1.6 }}>{body}</p>
          <div className="rounded-xl p-4 mb-5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <p className="text-sm mb-1" style={{ color: '#166534', fontWeight: 700 }}>✓ Your approval is still active</p>
            <p className="text-sm" style={{ color: '#166534', fontWeight: 700 }}>✓ No new offer fee required for your next offer</p>
          </div>
          <button onClick={() => dismiss(true)} disabled={offerRejectedDismissing}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mb-2"
            style={{ backgroundColor: offerRejectedDismissing ? '#93c5fd' : '#3a6ad9', cursor: offerRejectedDismissing ? 'wait' : 'pointer' }}>
            Browse Homes
          </button>
          <button onClick={() => dismiss(false)} disabled={offerRejectedDismissing}
            className="w-full py-2 text-sm" style={{ color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>
            I'll do this later
          </button>
        </div>
      </div>
    );
  })()}

  {/* ── SmartEstimate preview modal ── */}
  {calcModal && (
  <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
  <div className="safe-modal-box bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ '--sm-max': '90vh' }}>
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
  <div>
  <h2 className="text-lg font-bold text-black">SmartEstimate</h2>
  <p className="text-xs text-gray-400 mt-0.5">Preview your estimate</p>
  </div>
  <button onClick={() => setCalcModal(false)} className="text-gray-400 hover:text-gray-700 p-1">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
  </button>
  </div>
  <div className="overflow-y-auto flex-1 px-6 py-5">
  <SmartEstimatePreview />
  </div>
  </div>
  </div>
  )}

  {/* ── Application submitted confirmation modal ── */}
  {finishModal && (
  <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5" style={{ backgroundColor: '#eff3fe' }}>
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#3a6afd' }}>
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
  </div>
  {/* ⚠️ "Upload Your Documents", NOT "Finish Your Approval". The heading named
      an OUTCOME the borrower cannot perform — approval is Doorly's act, not
      theirs — while the one thing they can actually do sat in the body text.
      A modal that appears the moment an application is submitted should name
      the next action, and the button below already goes where that happens. */}
  <h2 className="text-2xl font-bold text-gray-900 mb-3">Upload Your Documents</h2>
  <p className="text-gray-600 text-sm leading-relaxed mb-6">
  We've received your application!
  <br /><br />
  To move forward, upload your required documents. Everything you need is ready in your dashboard.
  </p>
  <button
  onClick={() => {
  setFinishModal(false);
  navigate('/dashboard', { replace: true, state: {} });
  }}
  className="btn-primary btn-full hover:opacity-90"
  style={{ backgroundColor: '#3a6afd', color: '#fff' }}
  >
  <span>Go to My Dashboard</span>
  <span className="btn-arrow">
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
  </span>
  </button>
  </div>
  </div>
  )}

  {/* Resume-payment banner — surfaced when an offer-fee intent is stuck in
      requires_action (3DS challenge the borrower didn't finish). Clicking
      "Complete bank verification" reopens the Stripe modal on the same
      intent so they don't have to start the offer flow over. */}
  {(() => {
    const stuck = payments.find((p) => p.payment_type === 'offer_fee' && p.status === 'requires_action');
    if (!stuck) return null;
    return (
      <div style={{ background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: '16px', padding: '16px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#7a5200' }}>
            Bank verification needed to complete your offer fee payment
          </div>
          <div style={{ fontSize: 12, color: '#7a5200', marginTop: 2, lineHeight: 1.5 }}>
            Your bank requested an extra verification step that wasn't finished. No money has been charged yet — finish the verification to submit your offer.
          </div>
        </div>
        <button
          onClick={() => setResumeTransactionId(stuck.id)}
          style={{ padding: '10px 18px', background: '#fffd5b', color: '#1a1a1a', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Complete verification →
        </button>
      </div>
    );
  })()}

  {/* Offer-fee ACH RETURNED banner — the bank transfer didn't clear after the
      offer was already submitted (optimistic submission). Reflects the
      users.offer_fee_payment_failed state / the failed payment_transactions row
      the Stripe webhook writes. The offer stays active; the borrower must
      re-pay (a re-payment task is also created server-side). */}
  {(() => {
    const failed = payments.find((p) => p.payment_type === 'offer_fee' && p.status === 'failed');
    if (!failed) return null;
    return (
      <div style={{ background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '16px', padding: '16px 20px', marginBottom: '16px' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#9f1239' }}>
          Your purchase initiation deposit payment didn’t clear
        </div>
        <div style={{ fontSize: 12, color: '#9f1239', marginTop: 2, lineHeight: 1.5 }}>
          Your bank transfer (ACH) for the purchase initiation deposit was returned. Your offer is still in place,
          but you'll need to complete the payment again to keep it on track. See your tasks below, or contact{' '}
          <a href="mailto:support@godoorly.com" style={{ color: '#9f1239', fontWeight: 700 }}>support@godoorly.com</a>.
        </div>
      </div>
    );
  })()}

  {/* Payment History — stage-agnostic. Renders Stripe payment_transactions
      (offer fees, showing fees) AND the verified down-payment wire from
      /api/borrower/status. Section shows when either source has data. */}
  {(payments.length > 0 || status?.down_payment_verified) && (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '20px', marginBottom: '20px', marginTop: '20px' }}>
      <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Payment History</h3>
      {/* Down-payment wire — surfaced from users.down_payment_verified.
          Rendered first since it's the largest borrower-paid transaction;
          Stripe payments follow chronologically below. */}
      {status?.down_payment_verified && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
          <div>
            <div style={{ fontWeight: '700', color: '#111827' }}>Down Payment Wire</div>
            <div style={{ color: '#9ca3af', marginTop: '2px' }}>
              {status.down_payment_verified_at
                ? `Confirmed received by Doorly on ${new Date(status.down_payment_verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : 'Confirmed received by Doorly'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: '800', color: '#111827' }}>
              {status.down_payment_amount_sent != null
                ? `$${Number(status.down_payment_amount_sent).toLocaleString()}`
                : '—'}
            </div>
            <div style={{ fontSize: '11px', color: '#2d6a0a', fontWeight: '700' }}>Confirmed</div>
          </div>
        </div>
      )}
      {payments.map(p => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
          <div>
            <div style={{ fontWeight: '700', color: '#111827' }}>
              {p.payment_type === 'offer_fee' ? 'Purchase Initiation Deposit' : p.payment_type === 'showing_fee' ? 'Showing Fee' : p.payment_type}
            </div>
            <div style={{ color: '#9ca3af', marginTop: '2px' }}>
              {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: '800', color: '#111827' }}>${(p.amount_cents / 100).toFixed(2)}</div>
            <div style={{ fontSize: '11px', color: p.status === 'succeeded' ? '#2d6a0a' : '#ff082f', fontWeight: '700', textTransform: 'capitalize' }}>{p.status === 'requires_action' ? 'Verification needed' : p.status}</div>
          </div>
        </div>
      ))}
    </div>
  )}

  {/* Resume Payment Modal — opens when borrower clicks the banner above.
      On success, re-POSTs to /api/homes/offer with listing_id/address/notes
      pulled from the original intent's metadata, so the offer record
      finally lands in DoorlyDesk. */}
  <PaymentModal
    isOpen={!!resumeTransactionId}
    onClose={() => setResumeTransactionId(null)}
    type="offer_fee"
    resumeTransactionId={resumeTransactionId}
    onSuccess={async (paymentResult) => {
      const meta = paymentResult?.metadata || {};
      try {
        await axios.post(`${API_URL}/api/homes/offer`, {
          listing_id: meta.listing_id,
          address:    meta.address,
          notes:      meta.notes || undefined,
          offer_initiation_payment_method: 'stripe',
          payment_intent_id: paymentResult?.payment_intent_id,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('[Resume Offer] Post-payment submit error:', err.message);
      }
      setResumeTransactionId(null);
      // Refresh payment list so the banner disappears.
      try {
        const r = await axios.get(`${API_URL}/api/auth/my-payments`, { headers: { Authorization: `Bearer ${token}` } });
        setPayments(r.data.transactions || []);
      } catch (_) {}
    }}
  />

  {/* Download badges — web app only; hidden inside the native app */}
  {!isNativeApp() && (
  <div className="flex flex-col items-center" style={{ marginTop: 16, paddingTop: 24, paddingBottom: 24 }}>
    <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Take Doorly with you</p>
    <div className="flex flex-wrap items-center justify-center gap-2">
      <AppStoreBadge />
      <GooglePlayBadge />
    </div>
  </div>
  )}

  </div>
  </div>
  </AppShell>
  );
}

export default Dashboard;
